import React, { useState, useEffect, useRef, useContext } from 'react';
import ReactDOM from 'react-dom';
import { buildPresetPatch } from '../../utils/vehiclePresets';
import {
  Box, TextField, IconButton, Button, Typography, Tooltip, CircularProgress,
  Collapse, Checkbox, Popover, Switch, FormControlLabel, Paper, Chip,
  Select, MenuItem,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import { useSelector, useDispatch } from 'react-redux';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import DirectionsIcon from '@material-ui/icons/Directions';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty';
import ClearIcon from '@material-ui/icons/Clear';
import MyLocationIcon from '@material-ui/icons/MyLocation';
import FlagIcon from '@material-ui/icons/Flag';
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import SwapHorizIcon from '@material-ui/icons/SwapHoriz';
import HomeIcon from '@material-ui/icons/Home';
import WarningIcon from '@material-ui/icons/Warning';
import TuneIcon from '@material-ui/icons/Tune';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import CloseIcon from '@material-ui/icons/Close';
import ScheduleIcon from '@material-ui/icons/Schedule';
import SecurityIcon from '@material-ui/icons/Security';
import HotelIcon from '@material-ui/icons/Hotel';
import RoomIcon from '@material-ui/icons/Room';
import LocalGasStationIcon from '@material-ui/icons/LocalGasStation';
import BookmarkBorderIcon from '@material-ui/icons/BookmarkBorder';
import BookmarkIcon from '@material-ui/icons/Bookmark';
import StarIcon from '@material-ui/icons/Star';
import StarBorderIcon from '@material-ui/icons/StarBorder';
import UndoIcon from '@material-ui/icons/Undo';
import CompareArrowsIcon from '@material-ui/icons/CompareArrows';
import CallSplitIcon from '@material-ui/icons/CallSplit';
import AttachFileIcon from '@material-ui/icons/AttachFile';
import DescriptionIcon from '@material-ui/icons/Description';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import AssignmentIcon from '@material-ui/icons/Assignment';
import EuroSymbolIcon from '@material-ui/icons/EuroSymbol';
import * as XLSX from 'xlsx';
import {
  setOrigin, setDestination, patchDestination, addWaypoint, updateWaypoint, removeWaypoint, reorderWaypoints, setWaypoints,
  setRouteName, setCargoRef, setDepartureTime, setArrivalTimeWindow, setRoundTrip,
  addRouteMarker, updateRouteMarker, removeRouteMarker, clearRoute, undoRoute,
  setCalculating, setRouteResult, setRouteError, setRouteOptions,
  setFuelType, setFuelConsumption, setFuelPrice,
  selectCurrentRoute, selectCanUndo, setPreviewRoute,
  setPois, setEstimatedCost, saveRouteToAPI,
} from '../../store/slices/routesSlice';
import { selectVehicles } from '../../store/slices/vehiclesSlice';
import { addToast } from '../../store/slices/notificationsSlice';
import { setMapClickMode, setShowDetails, setNeedsRecalculation, setAutoSaveReturn, setActiveView, setResetMapView, setHoveredMarkerId, setHoveredSavedLocation, setDisplayedSavedRoutes, setHoveredCompareRouteIdx, setDetailsPanelOpen, selectUI } from '../../store/slices/uiSlice';
import { t } from '../../i18n';
import SectionTitle from '../common/SectionTitle';
import { AuthContext } from '../../App';
import { selectLocations, fetchLocations, createLocation } from '../../store/slices/locationsSlice';
import { selectDrivers } from '../../store/slices/driversSlice';
import { fetchTollData } from '../../store/slices/tollsSlice';
import { fetchFuelPrices, clearFuelPriceOverrides, selectFuelPrices, selectFuelPriceOverrides } from '../../store/slices/fuelPricesSlice';
import DateTimePicker from '../common/DateTimePicker';
import { geocodeSearchMulti, reverseGeocodePlace } from '../../utils/mapboxApi';
import { calculateRouteHERE, URBAN_AREAS_BY_COUNTRY } from '../../utils/hereApi';
import { calculateRouteGoogle } from '../../utils/googleRoutesApi';
import { calculateRouteHybrid } from '../../utils/hybridRoutesProvider';
import { validateRouteIncidents } from '../../utils/routeIncidentValidator';
import { CURRENCIES, getCurrencySymbol, getSavedFuelPrice, saveFuelPrice } from '../../utils/currencies';
import { resolveFuelPriceEUR } from '../../utils/resolveFuelPrice';
import { calculateStops } from '../../utils/tachograph';
import { fetchPoisAlongRoute } from '../../utils/overpassApi';
import RouteCompareDialog from '../common/RouteCompareDialog';
import AddOTToRouteDialog from '../Routes/AddOTToRouteDialog';
import { FUEL_TYPES } from './VehicleDriverForm';
import store from '../../store';

const useStyles = makeStyles(() => ({
  '@global': {
    '@keyframes spin': {
      from: { transform: 'rotate(0deg)' },
      to: { transform: 'rotate(360deg)' },
    },
    '@keyframes routeInfoPulse': {
      '0%, 100%': { boxShadow: '0 0 0 2px rgba(251,191,36,0.18)' },
      '50%':       { boxShadow: '0 0 0 4px rgba(251,191,36,0.35)' },
    },
  },
  root: { padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 12 },
  waypointRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  locationIcon: {
    width: 28,
    height: 28,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  connector: {
    width: 1,
    height: 12,
    background: 'rgba(0,0,0,0.12)',
    marginLeft: 13,
  },
  inputField: {
    flex: 1,
    '& .MuiInputBase-root': { fontSize: '0.82rem' },
    '& .MuiOutlinedInput-input': { padding: '8px 12px' },
  },
  autocomplete: {
    flex: 1,
    '& .MuiInputBase-root': { fontSize: '0.82rem', paddingRight: '8px !important' },
    '& .MuiOutlinedInput-input': { padding: '8px 12px !important' },
    '& .MuiAutocomplete-endAdornment': { right: 6, top: 'calc(50% - 10px)' },
  },
  mapPickBtn: {
    width: 28,
    height: 28,
    padding: 0,
    color: '#94a3b8',
    '&:hover': { color: '#3b82f6' },
  },
  addWpBtn: {
    fontSize: '0.72rem',
    color: '#64748b',
    textTransform: 'none',
    padding: '2px 0',
    '&:hover': { color: '#3b82f6' },
    justifyContent: 'flex-start',
  },
  calcBtn: {
    background: 'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)',
    color: '#ffffff',
    fontWeight: 700,
    fontSize: '0.82rem',
    padding: '10px 0',
    borderRadius: 10,
    letterSpacing: '0.03em',
    boxShadow: '0 4px 14px rgba(59,130,246,0.35)',
    transition: 'all 0.2s ease',
    '&:hover': {
      background: 'linear-gradient(135deg, #60a5fa 0%, #818cf8 100%)',
      boxShadow: '0 6px 18px rgba(59,130,246,0.45)',
      transform: 'translateY(-1px)',
    },
    '&:disabled': { background: '#e2e8f0', color: '#94a3b8', boxShadow: 'none', transform: 'none' },
  },
  calcBtnReady: {
    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    color: '#ffffff',
    fontWeight: 700,
    fontSize: '0.82rem',
    padding: '10px 0',
    borderRadius: 10,
    letterSpacing: '0.03em',
    boxShadow: '0 4px 14px rgba(16,185,129,0.35)',
    transition: 'all 0.2s ease',
    '&:hover': {
      background: 'linear-gradient(135deg, #34d399 0%, #10b981 100%)',
      boxShadow: '0 6px 18px rgba(16,185,129,0.45)',
      transform: 'translateY(-1px)',
    },
  },
  calcBtnCalculating: {
    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
    color: '#ffffff',
    fontWeight: 700,
    fontSize: '0.82rem',
    padding: '10px 0',
    borderRadius: 10,
    letterSpacing: '0.03em',
    boxShadow: '0 4px 14px rgba(245,158,11,0.35)',
    cursor: 'not-allowed',
  },
  clearBtn: {
    color: '#64748b',
    border: '1px solid rgba(0,0,0,0.12)',
    fontSize: '0.75rem',
    padding: '6px 0',
    '&:hover': { color: '#ef4444', borderColor: 'rgba(239,68,68,0.4)' },
  },
  optionItem: {
    display: 'flex',
    flexDirection: 'column',
    padding: '4px 0',
  },
  optionName: {
    fontSize: '0.8rem',
    color: '#0f172a',
    lineHeight: 1.3,
  },
  saveLocPopover: {
    padding: '12px 14px',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    minWidth: 220,
  },
  saveLocTitle: {
    fontSize: '0.75rem',
    fontWeight: 700,
    color: '#0f172a',
  },
  saveLocBtn: {
    fontSize: '0.72rem',
    textTransform: 'none',
    fontWeight: 600,
  },
}));

function GeocoderInput({ label, value, coordinates, onSelect, onMapPick, onRemove, onSave, icon: Icon, iconColor, lang, savedLocations, showFavoritePicker, plateTooltip }) {
  const classes = useStyles();
  const [options, setOptions] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [favAnchor, setFavAnchor] = useState(null);
  const [favSearch, setFavSearch] = useState('');
  const preferredFavorites = React.useMemo(() => {
    if (!Array.isArray(savedLocations)) return [];
    // "Preferencial" = favoritos marcados com isPreferred. Fallback: todos se não houver preferidos.
    const preferred = savedLocations.filter(l => l.isPreferred);
    return preferred.length > 0 ? preferred : savedLocations;
  }, [savedLocations]);
  const filteredFavs = React.useMemo(() => {
    if (!favSearch) return preferredFavorites;
    const q = favSearch.toLowerCase();
    return preferredFavorites.filter(l =>
      (l.name || '').toLowerCase().includes(q)
      || (l.address || '').toLowerCase().includes(q)
    );
  }, [preferredFavorites, favSearch]);
  const [saveAnchor, setSaveAnchor] = useState(null);
  const [saveType, setSaveType] = useState('poi');
  const [saveName, setSaveName] = useState('');
  const [saveNotes, setSaveNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const isAlreadySaved = coordinates && savedLocations?.some(
    loc => Math.abs(loc.latitude - coordinates[1]) < 0.0005 && Math.abs(loc.longitude - coordinates[0]) < 0.0005
  );

  // Sync input when value is set externally (e.g. map click)
  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  // Check if input looks like coordinates (lat,lng or lat lng)
  function parseCoords(text) {
    const match = text.trim().match(/^(-?\d+\.?\d*)[,\s]+(-?\d+\.?\d*)$/);
    if (!match) return null;
    const lat = parseFloat(match[1]);
    const lng = parseFloat(match[2]);
    if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) return { lat, lng };
    return null;
  }

  // Debounced geocoding (with coordinate detection)
  useEffect(() => {
    if (!inputValue || inputValue.length < 2) {
      setOptions([]);
      return;
    }
    // If input looks like coordinates, offer as direct option
    const coords = parseCoords(inputValue);
    if (coords) {
      setOptions([{ name: `${coords.lat}, ${coords.lng}`, coordinates: [coords.lng, coords.lat] }]);
      setLoading(false);
      return;
    }
    const timer = setTimeout(async () => {
      setLoading(true);
      const results = await geocodeSearchMulti(inputValue);
      setOptions(results);
      setLoading(false);
    }, 350);
    return () => clearTimeout(timer);
  }, [inputValue]); // eslint-disable-line

  async function handleSaveLocation() {
    if (!onSave || !coordinates) return;
    setSaving(true);
    await onSave({ name: saveName || inputValue, type: saveType, latitude: coordinates[1], longitude: coordinates[0], address: inputValue, notes: saveNotes || null });
    setSaving(false);
    setSaveAnchor(null);
    setSaveName('');
    setSaveNotes('');
  }

  return (
    <Box className={classes.waypointRow}>
      <Box className={classes.locationIcon} style={{ background: `${iconColor}18` }}>
        <Icon style={{ fontSize: 14, color: iconColor }} />
      </Box>
      <Autocomplete
        className={classes.autocomplete}
        options={options}
        getOptionLabel={(option) => typeof option === 'string' ? option : option.name}
        filterOptions={(x) => x}
        freeSolo
        loading={loading}
        inputValue={inputValue}
        onInputChange={(_, newValue) => {
          setInputValue(newValue);
        }}
        onChange={(_, newValue) => {
          if (newValue && typeof newValue === 'object') {
            onSelect(newValue);
          }
        }}
        noOptionsText={inputValue.length >= 2 ? 'Sem resultados' : 'Digite para pesquisar...'}
        loadingText="A pesquisar..."
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            size="small"
            label={label}
            placeholder={t(lang, 'enterAddress')}
            InputLabelProps={{ ...params.InputLabelProps, shrink: true, style: { fontSize: '0.75rem' } }}
            // Tooltip nativa com a matrícula quando a origem/destino veio de um
            // veículo do mapa (o texto mostra a morada/zona, não a matrícula).
            inputProps={{ ...params.inputProps, title: plateTooltip || params.inputProps?.title }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={12} style={{ marginRight: 4 }} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(option) => {
          // Tag each result with its source so the operator knows where
          // it came from. Google POIs typically have the best match
          // for businesses (pastelarias, restaurantes); Mapbox/HERE
          // are stronger for addresses; OSM fills the gaps.
          const srcCfg = ({
            google: { label: 'Places', color: '#1a73e8', bg: 'rgba(26,115,232,0.10)' },
            mapbox: { label: 'Mapbox', color: '#475569', bg: 'rgba(71,85,105,0.10)' },
            here:   { label: 'HERE',   color: '#0ea5e9', bg: 'rgba(14,165,233,0.10)' },
            osm:    { label: 'OSM',    color: '#16a34a', bg: 'rgba(22,163,74,0.10)' },
          })[option.source] || null;
          return (
            <Box className={classes.optionItem} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Typography className={classes.optionName} style={{ flex: 1, minWidth: 0 }}>{option.name}</Typography>
              {srcCfg && (
                <span style={{
                  fontSize: '0.52rem', fontWeight: 800,
                  padding: '1px 6px', borderRadius: 999,
                  color: srcCfg.color, background: srcCfg.bg,
                  letterSpacing: '0.04em', textTransform: 'uppercase',
                  flexShrink: 0,
                }}>
                  {srcCfg.label}
                </span>
              )}
            </Box>
          );
        }}
      />
      <Tooltip title="Marcar no mapa">
        <IconButton size="small" className={classes.mapPickBtn} onClick={onMapPick}>
          <MyLocationIcon style={{ fontSize: 14 }} />
        </IconButton>
      </Tooltip>
      {showFavoritePicker && (
        <Tooltip title={lang === 'pt' ? 'Escolher favorito' : 'Pick favorite'}>
          <IconButton
            size="small"
            className={classes.mapPickBtn}
            onClick={e => { setFavAnchor(e.currentTarget); setFavSearch(''); }}
            style={{ color: '#eab308' }}
          >
            <StarIcon style={{ fontSize: 15 }} />
          </IconButton>
        </Tooltip>
      )}
      {favAnchor && ReactDOM.createPortal(
        <>
          {/* Backdrop to dismiss */}
          <Box
            onClick={() => setFavAnchor(null)}
            style={{ position: 'fixed', inset: 0, zIndex: 9998, background: 'transparent' }}
          />
          <Box
            style={{
              position: 'fixed',
              top: favAnchor.getBoundingClientRect().bottom + 4,
              left: Math.max(8, favAnchor.getBoundingClientRect().right - 280),
              zIndex: 9999,
              background: '#fff',
              borderRadius: 12,
              width: 300,
              maxHeight: 380,
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 12px 40px rgba(15,23,42,0.2)',
              border: '1px solid #e2e8f0',
              overflow: 'hidden',
            }}
          >
            <Box style={{ padding: '8px 10px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
              <Typography style={{ fontSize: '0.72rem', fontWeight: 700, color: '#0f172a', marginBottom: 6 }}>
                {lang === 'pt' ? 'Favoritos preferenciais' : 'Preferred favorites'}
              </Typography>
              <TextField
                size="small" variant="outlined" fullWidth autoFocus
                placeholder={lang === 'pt' ? 'Pesquisar…' : 'Search…'}
                value={favSearch}
                onChange={e => setFavSearch(e.target.value)}
                inputProps={{ style: { fontSize: '0.74rem', padding: '6px 8px' } }}
              />
            </Box>
            <Box style={{ overflowY: 'auto', flex: 1 }}>
              {filteredFavs.length === 0 && (
                <Typography style={{ fontSize: '0.72rem', color: '#94a3b8', padding: '16px 10px', textAlign: 'center' }}>
                  {lang === 'pt' ? 'Sem favoritos' : 'No favorites'}
                </Typography>
              )}
              {filteredFavs.map(l => (
                <Box
                  key={l.id}
                  onClick={() => {
                    onSelect({ name: l.name, coordinates: [Number(l.longitude), Number(l.latitude)] });
                    setFavAnchor(null);
                  }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#f8fafc'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = ''; }}
                >
                  {l.isPreferred
                    ? <StarIcon style={{ fontSize: 14, color: '#eab308', flexShrink: 0 }} />
                    : <BookmarkIcon style={{ fontSize: 13, color: '#94a3b8', flexShrink: 0 }} />}
                  <Box style={{ flex: 1, minWidth: 0 }}>
                    <Typography style={{ fontSize: '0.76rem', fontWeight: 600, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {l.name}
                    </Typography>
                    {l.address && (
                      <Typography style={{ fontSize: '0.6rem', color: '#94a3b8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {l.address}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </>,
        document.body
      )}
      {onSave && coordinates && inputValue && (
        <Tooltip title={isAlreadySaved ? t(lang, 'locationAlreadySaved') : t(lang, 'saveLocation')}>
          <IconButton
            size="small"
            className={classes.mapPickBtn}
            onClick={e => { if (!isAlreadySaved) { setSaveAnchor(e.currentTarget); setSaveName(inputValue); setSaveNotes(''); } }}
            style={isAlreadySaved ? { color: '#22c55e' } : {}}
          >
            {isAlreadySaved ? <BookmarkIcon style={{ fontSize: 14 }} /> : <BookmarkBorderIcon style={{ fontSize: 14 }} />}
          </IconButton>
        </Tooltip>
      )}
      {onRemove && (
        <Tooltip title="Remover">
          <IconButton size="small" className={classes.mapPickBtn} onClick={onRemove}>
            <DeleteIcon style={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      )}
      <Popover
        open={Boolean(saveAnchor)}
        anchorEl={saveAnchor}
        onClose={() => setSaveAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Box style={{ padding: 14, width: 260 }}>
          <Typography style={{ fontSize: '0.78rem', fontWeight: 600, color: '#1e293b', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
            <BookmarkIcon style={{ fontSize: 15, color: '#3b82f6' }} />
            {lang === 'pt' ? 'Guardar como Favorito' : 'Save as Favorite'}
          </Typography>

          {/* Name */}
          <TextField
            size="small" variant="outlined" fullWidth
            label={t(lang, 'name')}
            value={saveName} onChange={e => setSaveName(e.target.value)}
            InputProps={{ style: { fontSize: '0.78rem' } }}
            InputLabelProps={{ shrink: true, style: { fontSize: '0.72rem' } }}
            style={{ marginBottom: 8 }}
          />

          {/* Type — icon buttons */}
          <Typography style={{ fontSize: '0.58rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
            {t(lang, 'type')}
          </Typography>
          <Box style={{ display: 'flex', gap: 4, marginBottom: 8, flexWrap: 'wrap' }}>
            {[
              { v: 'warehouse',  icon: HomeIcon,            color: '#6366f1', label: t(lang, 'typeWarehouse') },
              { v: 'safe_point', icon: SecurityIcon,        color: '#22c55e', label: t(lang, 'markerSafe') },
              { v: 'overnight',  icon: HotelIcon,           color: '#8b5cf6', label: t(lang, 'markerOvernight') },
              { v: 'fuel',       icon: LocalGasStationIcon, color: '#ef4444', label: t(lang, 'markerFuel') },
              { v: 'poi',        icon: RoomIcon,            color: '#f59e0b', label: t(lang, 'markerPoi') },
              { v: 'other',      icon: RoomIcon,            color: '#64748b', label: t(lang, 'other') },
            ].map(({ v, icon: Icon, color, label: lbl }) => {
              const active = saveType === v;
              return (
                <Tooltip key={v} title={lbl}>
                  <Box onClick={() => setSaveType(v)} style={{
                    display: 'flex', alignItems: 'center', gap: 4, padding: '4px 8px',
                    borderRadius: 6, cursor: 'pointer', fontSize: '0.62rem', fontWeight: 500,
                    background: active ? `${color}15` : '#f8fafc',
                    color: active ? color : '#94a3b8',
                    border: `1px solid ${active ? color + '40' : '#e2e8f0'}`,
                    transition: 'all 0.15s',
                  }}>
                    <Icon style={{ fontSize: 12 }} />
                    {lbl}
                  </Box>
                </Tooltip>
              );
            })}
          </Box>

          {/* Notes */}
          <TextField
            size="small" variant="outlined" fullWidth multiline rows={2}
            label={t(lang, 'locationNotes')}
            value={saveNotes} onChange={e => setSaveNotes(e.target.value)}
            InputProps={{ style: { fontSize: '0.75rem' } }}
            InputLabelProps={{ shrink: true, style: { fontSize: '0.72rem' } }}
            style={{ marginBottom: 10 }}
          />

          <Button
            variant="contained" size="small" fullWidth
            onClick={handleSaveLocation}
            disabled={saving || !saveName.trim()}
            style={{ fontSize: '0.75rem', background: '#3b82f6', color: '#fff', borderRadius: 8, textTransform: 'none', fontWeight: 600 }}
          >
            {saving ? <CircularProgress size={14} style={{ color: '#fff' }} /> : (lang === 'pt' ? 'Guardar Favorito' : 'Save Favorite')}
          </Button>
        </Box>
      </Popover>
    </Box>
  );
}

const MARKER_TYPES = [
  { type: 'safe',      labelKey: 'markerSafe',      icon: SecurityIcon,        color: '#22c55e' },
  { type: 'overnight', labelKey: 'markerOvernight',  icon: HotelIcon,           color: '#8b5cf6' },
  { type: 'poi',       labelKey: 'markerPoi',        icon: RoomIcon,            color: '#f59e0b' },
  { type: 'fuel',      labelKey: 'markerFuel',       icon: LocalGasStationIcon, color: '#ef4444' },
];

// Calculate distance along route from origin to a point (finds nearest route coord)
function distanceAlongRoute(routeCoords, point) {
  if (!routeCoords || !point) return null;
  const [pLng, pLat] = point;
  let minDist = Infinity;
  let nearestIdx = 0;
  for (let i = 0; i < routeCoords.length; i++) {
    const [cLng, cLat] = routeCoords[i];
    const d = (cLng - pLng) ** 2 + (cLat - pLat) ** 2;
    if (d < minDist) { minDist = d; nearestIdx = i; }
  }
  // Sum haversine segments from start to nearestIdx
  let total = 0;
  const R = 6371;
  for (let i = 1; i <= nearestIdx; i++) {
    const [x1, y1] = routeCoords[i - 1];
    const [x2, y2] = routeCoords[i];
    const dLat = (y2 - y1) * Math.PI / 180;
    const dLng = (x2 - x1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(y1 * Math.PI / 180) * Math.cos(y2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
    total += R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }
  return Math.round(total);
}

function haversineKm([lng1, lat1], [lng2, lat2]) {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// Location type config (for nearby locations in popup)
const LOCATION_TYPE_CONFIG = {
  safe_point: { icon: SecurityIcon,        color: '#22c55e' },
  overnight:  { icon: HotelIcon,           color: '#8b5cf6' },
  poi:        { icon: RoomIcon,            color: '#f59e0b' },
  fuel:       { icon: LocalGasStationIcon, color: '#ef4444' },
  garage:     { icon: RoomIcon,            color: '#3b82f6' },
  other:      { icon: RoomIcon,            color: '#64748b' },
};

// Map RouteForm marker types to ClientLocation DB types
const MARKER_TO_LOCATION_TYPE = { safe: 'safe_point', overnight: 'overnight', poi: 'poi', fuel: 'fuel' };

function RouteMarkersSection({ route, dispatch, lang, mapClickMode, onMapPick, onSave, savedLocations }) {
  const [expanded, setExpanded] = useState(false);
  const [locPopover, setLocPopover] = useState(null); // { anchorEl, markerType }
  const { token } = useContext(AuthContext);
  const locations = useSelector(selectLocations);
  const markers = route.routeMarkers || [];
  const routeCoords = route.computedRoute?.geometry?.coordinates;
  const totalKm = route.computedRoute ? Math.round(route.computedRoute.distance / 1000) : null;

  useEffect(() => {
    if (expanded && token && locations.length === 0) {
      dispatch(fetchLocations(token));
    }
  }, [expanded, token, locations.length, dispatch]);

  function handleMarkerBtnClick(e, type) {
    const locType = MARKER_TO_LOCATION_TYPE[type];
    const typeLocations = locations.filter(l => l.type === locType);
    if (typeLocations.length > 0) {
      setLocPopover({ anchorEl: e.currentTarget, markerType: type });
    } else {
      handleAddMarkerManual(type);
    }
  }

  function handleAddMarkerManual(type) {
    setLocPopover(null);
    const id = `m-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    dispatch(addRouteMarker({ id, type, name: '', coordinates: null, notes: '' }));
    dispatch(setMapClickMode(`marker-${id}`));
  }

  function handleUseSavedLocation(type, loc) {
    setLocPopover(null);
    const id = `m-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    dispatch(addRouteMarker({
      id, type,
      name: loc.name,
      coordinates: [Number(loc.longitude), Number(loc.latitude)],
      notes: loc.notes || '',
    }));
  }

  return (
    <Box style={{ marginTop: 4 }}>
      <Box
        onClick={() => setExpanded(e => !e)}
        style={{
          display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer',
          padding: '4px 0', color: '#64748b', fontSize: '0.72rem', fontWeight: 600, userSelect: 'none',
        }}
      >
        <RoomIcon style={{ fontSize: 14 }} />
        <Typography style={{ fontSize: '0.72rem', fontWeight: 600, flex: 1 }}>
          {t(lang, 'routeMarkers')} {markers.length > 0 && `(${markers.length})`}
        </Typography>
        {expanded ? <ExpandLessIcon style={{ fontSize: 16 }} /> : <ExpandMoreIcon style={{ fontSize: 16 }} />}
      </Box>
      <Collapse in={expanded}>
        <Box style={{ background: '#f8fafc', border: '1px solid rgba(0,0,0,0.07)', borderRadius: 8, padding: '8px 10px' }}>
          {/* Add marker buttons */}
          <Box style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, marginBottom: markers.length > 0 ? 8 : 0 }}>
            {MARKER_TYPES.map(({ type, labelKey, icon: MarkerIcon, color }) => (
              <Box
                key={type}
                onClick={(e) => handleMarkerBtnClick(e, type)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 4, padding: '5px 8px',
                  borderRadius: 6, cursor: 'pointer', fontSize: '0.65rem', fontWeight: 600,
                  background: `${color}10`, color, border: `1px solid ${color}30`,
                  transition: 'all 0.15s',
                }}
              >
                <MarkerIcon style={{ fontSize: 13 }} />
                {t(lang, labelKey)}
              </Box>
            ))}
          </Box>

          {/* Saved locations popover */}
          <Popover
            open={Boolean(locPopover)}
            anchorEl={locPopover?.anchorEl}
            onClose={() => setLocPopover(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            PaperProps={{ style: { borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.15)', maxHeight: 260, minWidth: 220, overflow: 'auto' } }}
          >
            <Box style={{ padding: 6 }}>
              <Typography style={{ fontSize: '0.65rem', fontWeight: 700, color: '#64748b', padding: '4px 8px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {t(lang, 'savedLocations')}
              </Typography>
              {locPopover && locations
                .filter(l => l.type === MARKER_TO_LOCATION_TYPE[locPopover.markerType])
                .map(loc => (
                  <Box
                    key={loc.id}
                    onClick={() => handleUseSavedLocation(locPopover.markerType, loc)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px',
                      borderRadius: 6, cursor: 'pointer', transition: 'background 0.15s',
                      fontSize: '0.75rem', fontWeight: 500, color: '#1e293b',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <RoomIcon style={{ fontSize: 14, color: MARKER_TYPES.find(m => m.type === locPopover.markerType)?.color || '#64748b' }} />
                    <Box style={{ flex: 1, minWidth: 0 }}>
                      <Typography style={{ fontSize: '0.75rem', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{loc.name}</Typography>
                      {loc.address && <Typography style={{ fontSize: '0.6rem', color: '#94a3b8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{loc.address}</Typography>}
                    </Box>
                  </Box>
                ))
              }
              <Box style={{ height: 1, background: '#f1f5f9', margin: '4px 0' }} />
              <Box
                onClick={() => locPopover && handleAddMarkerManual(locPopover.markerType)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px',
                  borderRadius: 6, cursor: 'pointer', fontSize: '0.72rem', fontWeight: 600, color: '#3b82f6',
                }}
                onMouseEnter={e => e.currentTarget.style.background = '#eff6ff'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <MyLocationIcon style={{ fontSize: 14 }} />
                {t(lang, 'clickMapToSetLocation')}
              </Box>
            </Box>
          </Popover>

          {/* Marker list */}
          {markers.map((marker, idx) => {
            const mt = MARKER_TYPES.find(t => t.type === marker.type) || MARKER_TYPES[2];
            const MarkerIcon = mt.icon;
            const isPickingThis = mapClickMode === `marker-${marker.id}`;
            const km = marker.coordinates && routeCoords ? distanceAlongRoute(routeCoords, marker.coordinates) : null;
            return (
              <Box
                key={marker.id}
                onMouseEnter={() => { dispatch(setHoveredMarkerId(marker.id)); if (marker.coordinates) dispatch(setHoveredSavedLocation({ lng: marker.coordinates[0], lat: marker.coordinates[1], name: marker.name || '' })); }}
                onMouseLeave={() => { dispatch(setHoveredMarkerId(null)); dispatch(setHoveredSavedLocation(null)); }}
                style={{
                  padding: '8px 10px',
                  borderLeft: `3px solid ${mt.color}`,
                  borderRadius: 8,
                  marginBottom: idx < markers.length - 1 ? 6 : 0,
                  background: `${mt.color}08`,
                  border: `1px solid ${mt.color}22`,
                  borderLeftWidth: 3,
                  borderLeftStyle: 'solid',
                  borderLeftColor: mt.color,
                  cursor: 'default',
                  transition: 'box-shadow 0.15s',
                }}
              >
                {/* Header */}
                <Box style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                  <Box style={{
                    width: 22, height: 22, borderRadius: '50%', display: 'flex',
                    alignItems: 'center', justifyContent: 'center',
                    background: `${mt.color}22`, color: mt.color, flexShrink: 0,
                  }}>
                    <MarkerIcon style={{ fontSize: 11 }} />
                  </Box>
                  <Typography style={{ fontSize: '0.68rem', fontWeight: 600, color: mt.color, flex: 1 }}>
                    {t(lang, mt.labelKey)}
                  </Typography>
                  <Tooltip title="Marcar no mapa">
                    <IconButton size="small" onClick={() => onMapPick(`marker-${marker.id}`)}
                      style={{ width: 20, height: 20, color: isPickingThis ? '#3b82f6' : '#94a3b8' }}>
                      <MyLocationIcon style={{ fontSize: 11 }} />
                    </IconButton>
                  </Tooltip>
                  {onSave && marker.coordinates && marker.name && (() => {
                    const alreadySaved = savedLocations?.some(
                      loc => Math.abs(loc.latitude - marker.coordinates[1]) < 0.0005 && Math.abs(loc.longitude - marker.coordinates[0]) < 0.0005
                    );
                    return (
                      <Tooltip title={alreadySaved ? t(lang, 'locationAlreadySaved') : t(lang, 'saveLocation')}>
                        <IconButton size="small"
                          onClick={() => !alreadySaved && onSave({
                            name: marker.name,
                            type: MARKER_TO_LOCATION_TYPE[marker.type] || 'poi',
                            latitude: marker.coordinates[1],
                            longitude: marker.coordinates[0],
                            address: marker.name,
                          })}
                          style={{ width: 20, height: 20, color: alreadySaved ? '#22c55e' : '#94a3b8' }}>
                          {alreadySaved ? <BookmarkIcon style={{ fontSize: 11 }} /> : <BookmarkBorderIcon style={{ fontSize: 11 }} />}
                        </IconButton>
                      </Tooltip>
                    );
                  })()}
                  <Tooltip title="Remover">
                    <IconButton size="small" onClick={() => dispatch(removeRouteMarker(marker.id))}
                      style={{ width: 20, height: 20, color: '#94a3b8' }}>
                      <DeleteIcon style={{ fontSize: 11 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
                {/* Name input */}
                <input
                  placeholder={`${t(lang, mt.labelKey)} ${idx + 1}`}
                  value={marker.name || ''}
                  onChange={e => dispatch(updateRouteMarker({ id: marker.id, name: e.target.value }))}
                  style={{
                    border: 'none', background: 'transparent', fontSize: '0.72rem', color: '#0f172a',
                    outline: 'none', width: '100%', padding: '2px 0', fontWeight: 500,
                  }}
                />
                {/* Info row: coordinates + distance */}
                {marker.coordinates && (
                  <Box style={{ display: 'flex', gap: 10, marginTop: 4, flexWrap: 'wrap' }}>
                    <Typography style={{ fontSize: '0.58rem', color: '#94a3b8' }}>
                      {marker.coordinates[1].toFixed(4)}, {marker.coordinates[0].toFixed(4)}
                    </Typography>
                    {km !== null && (
                      <Typography style={{ fontSize: '0.58rem', color: mt.color, fontWeight: 600 }}>
                        {km} km da origem{totalKm ? ` · ${totalKm - km} km ao destino` : ''}
                      </Typography>
                    )}
                  </Box>
                )}
                {isPickingThis && (
                  <Typography style={{ fontSize: '0.6rem', color: '#3b82f6', fontWeight: 500, marginTop: 2 }}>
                    Clique no mapa para marcar...
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>
      </Collapse>
    </Box>
  );
}

function CollapsibleSection({ title, icon, iconColor, iconBg, open, onToggle, badge, children }) {
  return (
    <Box style={{ marginBottom: 0 }}>
      <Box
        onClick={onToggle}
        style={{
          display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0',
          cursor: 'pointer', userSelect: 'none',
        }}
      >
        <Box style={{
          width: 24, height: 24, borderRadius: 7,
          background: iconBg || '#eef2ff',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          {icon}
        </Box>
        <Typography style={{ fontSize: '0.72rem', fontWeight: 700, color: '#1e293b', flex: 1 }}>
          {title}
        </Typography>
        {badge}
        {open
          ? <ExpandLessIcon style={{ fontSize: 16, color: '#94a3b8' }} />
          : <ExpandMoreIcon style={{ fontSize: 16, color: '#94a3b8' }} />}
      </Box>
      <Collapse in={open}>
        <Box style={{ paddingLeft: 2, paddingBottom: 4 }}>
          {children}
        </Box>
      </Collapse>
    </Box>
  );
}

function RouteForm() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const route = useSelector(selectCurrentRoute);
  const canUndo = useSelector(selectCanUndo);
  const { lang, mapClickMode, needsRecalculation, operationOrigin, autoSaveReturn } = useSelector(selectUI);
  const { token, modules, clientSettings } = useContext(AuthContext);
  // Variante mercadoria (Casais): custo editável por ponto + sync na OT.
  const isMercadoria = clientSettings?.uiVariant === 'mercadoria';
  // NOTA: existia aqui um `hasPois` que era calculado e NUNCA usado — o módulo
  // 'pois' não gateava nada. O gate real vive agora no MapViewGoogle, à volta
  // do RouteServicesPanel (a funcionalidade que o operador vê como "POIs").
  // O fetch Overpass abaixo NÃO é gateado de propósito: alimenta as paragens
  // obrigatórias do tacógrafo, que são outra peça.
  const savedLocations = useSelector(selectLocations);
  const driversForRoute = useSelector(selectDrivers);
  // Transport orders list — used to detect assistance-derived routes so the
  // origin warehouse chips can be hidden in that case.
  const transportOrdersList = useSelector(state => state.transportOrders?.list || []);
  const vehiclesList = useSelector(selectVehicles);
  const fuelPrices = useSelector(selectFuelPrices);
  const fuelOverrides = useSelector(selectFuelPriceOverrides);
  const nameInputRef = useRef(null);
  const handleCalculateRef = useRef(null);

  // Popup state
  const [showFavWpPicker, setShowFavWpPicker] = useState(false);
  // Dialog "Adicionar OT à Rota" — accionado pelo botão + OT ao lado
  // de "Adicionar paragem intermédia". Só está disponível quando a rota
  // já foi gravada como draft.
  const [addOTDialogOpen, setAddOTDialogOpen] = useState(false);

  // Incidentes críticos sobre a rota recém-calculada (Fase 2 da
  // detecção incidents-aware). Preenchido após cada cálculo bem
  // sucedido — banner amarelo aparece se length>0. null = ainda
  // não validado / sem rota; [] = validado mas sem incidentes.
  const [routeIncidents, setRouteIncidents] = useState(null);
  const [routeIncidentsExpanded, setRouteIncidentsExpanded] = useState(false);
  const [showOTPicker, setShowOTPicker] = useState(false);
  const [pendingOTs, setPendingOTs] = useState([]);
  const [otLoading, setOtLoading] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);
  const [compareMode, setCompareMode] = useState(false);
  // Toggle leve: pede alternativas ao provider e pinta-as no mapa em
  // tracejado azul-claro (estilo Google Maps), sem abrir o diálogo de
  // comparação. O DEFAULT vem do Perfil (Preferências → Rotas →
  // "Mostrar rotas alternativas") — default LIGADO. O operador pode
  // desligar pontualmente nesta sessão de cálculo.
  const [showAlternatives, setShowAlternatives] = useState(clientSettings?.showRouteAlternatives !== false);
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const [compareOpen, setCompareOpen] = useState(false);
  const [compareRoutes, setCompareRoutes] = useState([]);
  const [compareLoading, setCompareLoading] = useState(false);
  const [bestRouteIdx, setBestRouteIdx] = useState(0);
  // Rota atualmente APLICADA no mapa a partir do diálogo de comparação (o
  // operador pode ir experimentando várias sem fechar). null = nenhuma ainda.
  const [selectedCompareIdx, setSelectedCompareIdx] = useState(null);
  const [popupTab, setPopupTab] = useState('options');
  // Sidebar is 400 px wide + 12 px left gutter → give the popup clearance so it doesn't overlap
  const [popupPos, setPopupPos] = useState({ x: 432, y: 54 });
  const [openSections, setOpenSections] = useState({
    route: true,
    vehicle: false,
    options: false,
    transportCost: false,
  });
  const toggleSection = (key) => setOpenSections(s => ({ ...s, [key]: !s[key] }));
  const dragRef = useRef(null);

  function onPopupHeaderMouseDown(e) {
    e.preventDefault();
    dragRef.current = { startX: e.clientX, startY: e.clientY, initX: popupPos.x, initY: popupPos.y };
    function onMove(ev) {
      if (!dragRef.current) return;
      setPopupPos({ x: dragRef.current.initX + ev.clientX - dragRef.current.startX, y: dragRef.current.initY + ev.clientY - dragRef.current.startY });
    }
    function onUp() { dragRef.current = null; window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); }
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }

  // Route options (popup)
  const opts = route.routeOptions || {};
  const currency = opts.currency || 'EUR';
  const fuelType = route.fuelType || 'diesel';
  // Preço base do campo €: mostra o PREÇO POR PAÍS das Preferências para o país
  // do tenant (coerente com o que é mesmo usado no cálculo); só cai na cache do
  // browser quando não há preço por país definido. Função única reutilizada
  // aqui e no efeito de mudança de moeda/combustível.
  const seedFuelPrice = React.useCallback((cur, ft) => {
    const cc = clientSettings?.defaultCountry;
    const entry = cc && (clientSettings?.fuelPricesByCountry || {})[cc];
    const v = entry && entry[ft];
    if (v != null && isFinite(Number(v)) && Number(v) > 0) return Number(v);
    return getSavedFuelPrice(cur, ft);
  }, [clientSettings]);
  const defaultPrice = seedFuelPrice(currency, fuelType);
  const [priceInput, setPriceInput] = useState(String(route.fuelPrice ?? defaultPrice));
  const prevCurrencyRef = useRef(currency);
  const prevFuelTypeRef = useRef(fuelType);
  const [openCountries, setOpenCountries] = useState({});

  // Document upload state
  const [documents, setDocuments] = useState([]);
  const [docUploading, setDocUploading] = useState(null); // waypointIndex being uploaded
  const fileInputRef = useRef(null);
  const [docUploadTarget, setDocUploadTarget] = useState(null); // { waypointIndex }

  // Excel import state
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importData, setImportData] = useState([]); // parsed rows: { address, stopDuration, notes }
  const [importProgress, setImportProgress] = useState(null); // { current, total, status: 'parsing'|'geocoding'|'done' }
  const [importErrors, setImportErrors] = useState([]);
  const [importResults, setImportResults] = useState(null); // { imported, failed }
  const excelInputRef = useRef(null);

  // Fetch documents when route has dbId
  useEffect(() => {
    if (!route.dbId || !token) { setDocuments([]); return; }
    fetch(`/planner-api/routes/${route.dbId}/documents`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : [])
      .then(docs => setDocuments(docs))
      .catch(() => setDocuments([]));
  }, [route.dbId, token]);

  async function handleDocUpload(file, waypointIndex) {
    if (!route.dbId || !token || !file) return;
    setDocUploading(waypointIndex);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('waypointIndex', String(waypointIndex));
      formData.append('documentType', 'outro');
      const res = await fetch(`/planner-api/routes/${route.dbId}/documents`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!res.ok) throw new Error('Upload failed');
      const doc = await res.json();
      setDocuments(prev => [...prev, doc]);
    } catch (err) {
      console.error('Document upload error:', err);
      alert(t(lang, 'documentUploadError'));
    } finally {
      setDocUploading(null);
    }
  }

  async function handleDocDelete(docId) {
    if (!route.dbId || !token) return;
    try {
      const res = await fetch(`/planner-api/routes/${route.dbId}/documents/${docId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Delete failed');
      setDocuments(prev => prev.filter(d => d.id !== docId));
    } catch (err) {
      console.error('Document delete error:', err);
    }
  }

  function openFilePickerForWaypoint(waypointIndex) {
    if (!route.dbId) {
      alert(t(lang, 'routeNotSavedForDocs'));
      return;
    }
    setDocUploadTarget({ waypointIndex });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  }

  function handleFileInputChange(e) {
    const file = e.target.files?.[0];
    if (file && docUploadTarget !== null) {
      handleDocUpload(file, docUploadTarget.waypointIndex);
    }
    setDocUploadTarget(null);
  }

  // Excel import — parse file
  function handleExcelFileSelect(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' });

        if (!rows || rows.length === 0) {
          alert(lang === 'pt' ? 'Ficheiro vazio ou sem dados válidos.' : 'Empty file or no valid data.');
          return;
        }

        // Find column mappings (flexible headers)
        const headers = Object.keys(rows[0]);
        const addressCol = headers.find(h => /^(morada|address|local|endere[cç]o|dire[cç][aã]o|destino|ponto)$/i.test(h.trim()));
        const durationCol = headers.find(h => /^(tempo|duration|dura[cç][aã]o|paragem|stop|min|minutos)$/i.test(h.trim()));
        const notesCol = headers.find(h => /^(notas|notes|observa[cç][oõ]es|obs|coment[aá]rio)$/i.test(h.trim()));

        if (!addressCol) {
          // If no recognized header, use the first column as address
          const firstCol = headers[0];
          if (!firstCol) {
            alert(lang === 'pt' ? 'Não foi possível identificar a coluna de moradas.' : 'Could not identify address column.');
            return;
          }
          // Use first column as address
          const parsed = rows
            .map(row => ({
              address: String(row[firstCol] || '').trim(),
              stopDuration: durationCol ? parseInt(row[durationCol]) || null : null,
              notes: notesCol ? String(row[notesCol] || '').trim() : '',
            }))
            .filter(r => r.address.length > 0);

          setImportData(parsed);
          setImportErrors([]);
          setImportResults(null);
          setImportProgress(null);
          setShowImportDialog(true);
          return;
        }

        const parsed = rows
          .map(row => ({
            address: String(row[addressCol] || '').trim(),
            stopDuration: durationCol ? parseInt(row[durationCol]) || null : null,
            notes: notesCol ? String(row[notesCol] || '').trim() : '',
          }))
          .filter(r => r.address.length > 0);

        if (parsed.length === 0) {
          alert(lang === 'pt' ? 'Nenhuma morada válida encontrada.' : 'No valid addresses found.');
          return;
        }

        setImportData(parsed);
        setImportErrors([]);
        setImportResults(null);
        setImportProgress(null);
        setShowImportDialog(true);
      } catch (err) {
        console.error('Excel parse error:', err);
        alert(lang === 'pt' ? 'Erro ao ler ficheiro Excel.' : 'Error reading Excel file.');
      }
    };
    reader.readAsArrayBuffer(file);
  }

  // Excel import — geocode and add waypoints
  async function handleImportWaypoints() {
    if (!importData || importData.length === 0) return;

    const errors = [];
    let imported = 0;
    setImportProgress({ current: 0, total: importData.length, status: 'geocoding' });

    for (let i = 0; i < importData.length; i++) {
      const row = importData[i];
      setImportProgress({ current: i + 1, total: importData.length, status: 'geocoding' });

      try {
        const results = await geocodeSearchMulti(row.address);
        if (results && results.length > 0) {
          const best = results[0];
          dispatch(addWaypoint({
            name: best.name || row.address,
            coordinates: best.coordinates,
            stopDuration: row.stopDuration || null,
            notes: row.notes || '',
          }));
          imported++;
        } else {
          errors.push({ address: row.address, reason: lang === 'pt' ? 'Morada não encontrada' : 'Address not found' });
        }
      } catch (err) {
        errors.push({ address: row.address, reason: err.message || 'Erro' });
      }

      // Small delay to avoid rate limits
      if (i < importData.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 200));
      }
    }

    dispatch(setNeedsRecalculation(true));
    setImportErrors(errors);
    setImportResults({ imported, failed: errors.length });
    setImportProgress({ current: importData.length, total: importData.length, status: 'done' });
  }

  // Nearby locations (popup)
  const [maxDist, setMaxDist] = useState(30);

  // Auto-fill removido: anteriormente, se o cliente tinha exactamente 1
  // favorito do tipo 'garage', o planner preenchia a origem com esse
  // garage automaticamente. Confundia o operador (pin aparecia sem ter
  // sido pedido) e entrava em conflito com a lógica de consolidação
  // (driverHome / GPS / pickup) que decide a origem de rotas OT-derivadas.
  // Origem é agora escolhida sempre explicitamente — manualmente via
  // textbox/⭐ favoritos/📍 mapa, ou pelos defaults do "Atribuir veículo".

  // Client preferences are authoritative for currency.
  // The tenant's defaultCurrency drives the planner UI; origin location no longer overrides it.
  // If the client has no preference set, fall back to an Angola heuristic for the origin.
  useEffect(() => {
    const defaultCur = clientSettings?.defaultCurrency;
    if (defaultCur) {
      if ((opts.currency || 'EUR') !== defaultCur) {
        dispatch(setRouteOptions({ currency: defaultCur }));
      }
      return;
    }
    // Fallback (no client preference) — infer from origin
    const coords = route.origin?.coordinates;
    if (!coords) return;
    const [lng, lat] = coords;
    const inAngola = lng >= 11.5 && lng <= 24.1 && lat >= -18.1 && lat <= -4.4;
    const targetCurrency = inAngola ? 'AOA' : 'EUR';
    if ((opts.currency || 'EUR') !== targetCurrency) {
      dispatch(setRouteOptions({ currency: targetCurrency }));
    }
  }, [clientSettings?.defaultCurrency, route.origin?.coordinates]); // eslint-disable-line

  // Client tachograph preference is authoritative.
  // Whenever the client has it off, force the route option off too — covers cases where
  // Tachograph effective state — driven by the client preference + vehicle
  // type, with a per-route operator override on top:
  //   · client ON (or undefined / not yet configured)  → tacho ON for any
  //     non-light vehicle (car excluded)
  //   · client explicitly OFF  → tacho OFF, but the operator can flip the
  //     toggle to enable it just for this route
  //   · operator clicked toggle  → wins until the route is cleared
  // We sync routeOptions.useTachograph automatically whenever the client
  // preference or the vehicle type changes, *unless* the operator has
  // explicitly overridden via the toggle (`tachoOverride: true`).
  const clientTachoPref = clientSettings?.useTachograph;
  useEffect(() => {
    if (clientSettings === undefined) return;
    if (route.computedRoute) return;
    if (opts.tachoOverride === true) return;          // operator chose
    const isLight = opts.vehicleType === 'car';
    // Opt-in estrito: só ON com preferência EXPLICITAMENTE `true`. Antes era
    // "default-on quando indefinido", o que mantinha tacho activo durante
    // o intervalo entre o mount e o /auth/me devolver clientSettings,
    // suficiente para o operador clicar "Calcular" e contaminar a rota.
    // Alinha com o gate do separador "Paragens" no SidebarPanel.
    const clientUsesTacho = clientTachoPref === true;
    const expected = !isLight && clientUsesTacho;
    if (opts.useTachograph !== expected) {
      dispatch(setRouteOptions({ useTachograph: expected }));
    }
    // `opts.useTachograph` TEM de estar nas deps: sem ele, repor a rota ao
    // estado inicial (Limpar Rota / nova rota) devolvia o valor default sem
    // re-disparar este efeito — nenhuma das outras deps mudava — e o tacógrafo
    // ficava ligado num cliente que o tem desligado no Perfil.
  }, [clientTachoPref, opts.vehicleType, opts.tachoOverride, opts.useTachograph, route.computedRoute]); // eslint-disable-line

  // Apply the client's route defaults whenever the route doesn't yet have a vehicle sub-type
  // of its own. Uses `opts.vehicleSubType` as the sentinel so:
  //   · new session with empty route → defaults get applied
  //   · operator clicks a sub-type icon → their choice wins, effect no-ops
  //   · admin changes preferences mid-session and the route is still fresh → new defaults apply
  //   · loading a saved route that already has a sub-type → we don't overwrite it
  useEffect(() => {
    if (!clientSettings) return;
    const subType = clientSettings.defaultVehicleSubType;
    if (!subType) return;
    if (opts.vehicleSubType) return;          // already set (saved route or manual pick)
    if (route.computedRoute) return;          // already calculated — don't surprise-change

    const dimOverrides = {
      truckWeight: clientSettings.truckWeight,
      truckAxleLoad: clientSettings.truckAxleLoad,
      truckHeight: clientSettings.truckHeight,
      truckWidth: clientSettings.truckWidth,
      truckLength: clientSettings.truckLength,
    };
    const patch = buildPresetPatch(subType, dimOverrides);
    if (clientSettings.avoidTolls != null) patch.avoidTolls = !!clientSettings.avoidTolls;
    if (clientSettings.avoidFerries != null) patch.avoidFerries = !!clientSettings.avoidFerries;
    if (clientSettings.avoidMotorways != null) patch.avoidMotorways = !!clientSettings.avoidMotorways;
    // Restrições que antes só existiam no diálogo de rota, agora vindas do perfil.
    if (clientSettings.avoidTunnels != null) patch.avoidTunnels = !!clientSettings.avoidTunnels;
    if (clientSettings.avoidLEZ != null) patch.avoidLEZ = !!clientSettings.avoidLEZ;

    dispatch(setRouteOptions(patch));
    if (clientSettings.defaultConsumption != null && route.fuelConsumption == null) {
      dispatch(setFuelConsumption(Number(clientSettings.defaultConsumption)));
    }
    // Combustível por defeito do perfil (Gasóleo/Gasolina) — só na rota fresca.
    if (clientSettings.defaultFuelType) {
      dispatch(setFuelType(clientSettings.defaultFuelType));
    }
  }, [clientSettings?.defaultVehicleSubType, opts.vehicleSubType, route.computedRoute]); // eslint-disable-line

  // When currency or fuelType changes, load saved price
  useEffect(() => {
    const currencyChanged = prevCurrencyRef.current !== currency;
    const fuelChanged = prevFuelTypeRef.current !== fuelType;
    if (currencyChanged || fuelChanged) {
      const saved = seedFuelPrice(currency, fuelType);
      setPriceInput(String(saved));
      dispatch(setFuelPrice(saved));
      prevCurrencyRef.current = currency;
      prevFuelTypeRef.current = fuelType;
    }
  }, [currency, fuelType]); // eslint-disable-line

  // Load locations when nearby tab opens
  useEffect(() => {
    if (popupOpen && popupTab === 'nearby' && token && savedLocations.length === 0) {
      dispatch(fetchLocations(token));
    }
  }, [popupOpen, popupTab, token, savedLocations.length, dispatch]);

  // Close popup on Escape
  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') setPopupOpen(false); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Nearby locations computation
  const routeCoords = route.computedRoute?.geometry?.coordinates;
  const nearbyLocations = React.useMemo(() => {
    if (!routeCoords || savedLocations.length === 0) return [];
    const sampled = routeCoords.filter((_, i) => i % 10 === 0 || i === routeCoords.length - 1);
    return savedLocations
      .map(loc => {
        const coord = [Number(loc.longitude), Number(loc.latitude)];
        let minD = Infinity;
        for (const rc of sampled) {
          const d = haversineKm(coord, rc);
          if (d < minD) minD = d;
        }
        return { ...loc, distToRoute: Math.round(minD * 10) / 10 };
      })
      .filter(loc => loc.distToRoute <= maxDist)
      .sort((a, b) => {
        const kmA = distanceAlongRoute(routeCoords, [Number(a.longitude), Number(a.latitude)]);
        const kmB = distanceAlongRoute(routeCoords, [Number(b.longitude), Number(b.latitude)]);
        return (kmA || 0) - (kmB || 0);
      });
  }, [routeCoords, savedLocations, maxDist]);

  function toggleOpt(key) {
    dispatch(setRouteOptions({ [key]: !opts[key] }));
  }

  function openPopup(tab) {
    setPopupTab(tab);
    setPopupOpen(true);
  }

  const canCalculate =
    route.origin?.coordinates &&
    route.destination?.coordinates &&
    route.status !== 'calculating';

  // Keep ref current so the drag-recalculation useEffect always calls the latest version
  handleCalculateRef.current = handleCalculate;

  // Auto-recalculate when a drag waypoint was inserted via the map
  useEffect(() => {
    if (needsRecalculation && canCalculate) {
      dispatch(setNeedsRecalculation(false));
      handleCalculateRef.current();
    }
  }, [needsRecalculation]); // eslint-disable-line

  // Piloto automático (OT fuel) — quando o "Fim de carregamento" pede o modo
  // auto, esperamos que o recálculo termine e gravamos a rota + voltamos à
  // cronologia, sem o operador tocar no planeador. O ref "armed" garante que só
  // gravamos DEPOIS de um recálculo real (viu 'calculating'→'ready'), nunca no
  // 'ready' que sobra da rota carregada antes do recálculo.
  const autopilotArmedRef = useRef(false);
  useEffect(() => {
    if (!autoSaveReturn) { autopilotArmedRef.current = false; return; }
    if (route.status === 'calculating') { autopilotArmedRef.current = true; return; }
    if (route.status === 'error') {
      autopilotArmedRef.current = false;
      dispatch(setAutoSaveReturn(false));
      dispatch(addToast({ type: 'error', message: lang === 'pt' ? '❌ Não consegui calcular a rota automaticamente. Verifica os pontos no planeamento.' : '❌ Could not auto-calculate the route. Check the points in the planner.' }));
      return;
    }
    if (route.status === 'ready' && autopilotArmedRef.current
        && route.origin?.coordinates && route.destination?.coordinates) {
      autopilotArmedRef.current = false; // trava a re-entrada; o overlay fica até ao fim
      (async () => {
        const finalName = route.name || 'Rota sem nome';
        const result = await dispatch(saveRouteToAPI({ token, route: { ...route, name: finalName }, dispatch: false }));
        if (result.error) {
          dispatch(addToast({ type: 'error', message: lang === 'pt' ? '❌ Rota calculada mas não gravada — abre o planeamento para gravar.' : '❌ Route calculated but not saved — open the planner to save.' }));
        } else {
          dispatch(addToast({ type: 'success', message: lang === 'pt' ? '✅ Rota calculada e gravada.' : '✅ Route calculated and saved.' }));
          dispatch(setActiveView('orders'));
        }
        dispatch(setAutoSaveReturn(false)); // esconde o overlay já na cronologia
      })();
    }
  }, [autoSaveReturn, route.status]); // eslint-disable-line

  // Rede de segurança do piloto automático — se o cálculo não resolver em 30s,
  // liberta o overlay e avisa (fica no planeador para o operador gravar à mão).
  useEffect(() => {
    if (!autoSaveReturn) return;
    const timer = setTimeout(() => {
      autopilotArmedRef.current = false;
      dispatch(setAutoSaveReturn(false));
      dispatch(addToast({ type: 'warning', message: lang === 'pt' ? '⚠ O cálculo automático demorou demais. Grava a rota no planeamento.' : '⚠ Auto-calculation took too long. Save the route in the planner.' }));
    }, 30000);
    return () => clearTimeout(timer);
  }, [autoSaveReturn]); // eslint-disable-line

  // Safety net — external requests to recalculate (e.g. after consolidation from Ordens).
  // The "needsRecalculation" flag can miss its transition when the RouteForm mounts right
  // after the flag is already true. The custom event bypasses that race: the listener is
  // attached as soon as the form mounts and any caller can trigger a recalc directly.
  useEffect(() => {
    function onExternalRecalc() {
      if (handleCalculateRef.current && route.origin?.coordinates && route.destination?.coordinates) {
        handleCalculateRef.current();
      }
    }
    window.addEventListener('requestRouteRecalc', onExternalRecalc);
    return () => window.removeEventListener('requestRouteRecalc', onExternalRecalc);
  }, [route.origin?.coordinates, route.destination?.coordinates]);

  function calcFuelCost(routeData, prices, overrides) {
    const fuelType = route.fuelType || 'diesel';
    const fuelDef = FUEL_TYPES.find(f => f.value === fuelType) || FUEL_TYPES[0];
    const vehicleType = route.routeOptions?.vehicleType || 'truck';
    const defaultConsumption = vehicleType === 'car'
      ? Math.round(fuelDef.consumptionDefault * 0.38)
      : fuelDef.consumptionDefault;
    const consumption = route.fuelConsumption ?? defaultConsumption;
    const pricePerUnit = route.fuelPrice ?? fuelDef.priceDefault;
    const countryDistances = routeData.countryDistances;
    const fp = prices || fuelPrices;
    const fo = overrides || fuelOverrides;
    const routeRate = CURRENCIES.find(c => c.code === currency)?.rate || 1;

    // Preço por país via resolveFuelPriceEUR — EXACTAMENTE a mesma hierarquia
    // do detalhe (RouteDetails/FuelCountryPanel), para o comparador de rotas
    // bater certo com o custo autoritário. resolveFuelPriceEUR devolve EUR →
    // converte-se para a moeda do tenant (× routeRate), que é o que o diálogo
    // de comparação mostra (currencySymbol do tenant).
    if (countryDistances && Object.keys(countryDistances).length > 0) {
      const singleCountry = Object.keys(countryDistances).length === 1;
      let totalEUR = 0;
      for (const [cc, distM] of Object.entries(countryDistances)) {
        const distKm = distM / 1000;
        const fuel = (distKm / 100) * consumption;
        const priceEUR = resolveFuelPriceEUR(cc, fuelType, {
          overrides: fo, clientSettings, route, fuelPrices: fp,
          routeRate, fuelDef, singleCountry,
        });
        totalEUR += fuel * priceEUR;
      }
      return totalEUR * routeRate;
    }

    // Fallback (sem dados por país): preço único da sidebar (já na moeda do tenant).
    const distKm = (routeData.distance || 0) / 1000;
    return (distKm / 100) * consumption * pricePerUnit;
  }

  // Posição GPS actual do veículo da rota, no formato origem
  // ({ name, coordinates:[lng,lat] }). Usada para re-otimizar uma rota em
  // curso a partir de onde o motorista realmente está. Devolve null quando
  // não há veículo associado, posição válida ou fix recente (>15 min) — o
  // chamador recorre então à última paragem entregue / origem original.
  function getLiveVehicleOrigin() {
    const vid = route.vehicleId;
    const plate = route.vehiclePlate;
    let veh = null;
    if (vid != null && vid !== '') veh = vehiclesList.find(v => Number(v.id) === Number(vid));
    if (!veh && plate) veh = vehiclesList.find(v => (v.plate || '').toUpperCase() === String(plate).toUpperCase());
    const pos = veh?.position;
    const lat = Number(pos?.latitude);
    const lng = Number(pos?.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    if (pos.fixTime) {
      const ageMin = (Date.now() - new Date(pos.fixTime).getTime()) / 60000;
      if (ageMin > 15) return null; // fix demasiado antigo — não é de confiança
    }
    return {
      name: lang === 'pt' ? 'Posição actual do veículo' : 'Current vehicle position',
      coordinates: [lng, lat],
    };
  }

  // Mercadoria: ao calcular/recalcular, sincroniza o custo editado por ponto
  // (agreedPrice) para a OT respetiva. Fire-and-forget (não bloqueia o cálculo).
  const syncPointCostsToOTs = () => {
    if (!isMercadoria || !token) return;
    const pts = [...(route.waypoints || []), route.destination].filter(Boolean);
    const seen = new Set();
    for (const p of pts) {
      const otId = p?.otId;
      if (!otId || seen.has(otId) || p.agreedPrice == null) continue;
      seen.add(otId);
      fetch(`/planner-api/transport-orders/${otId}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ agreedPrice: Number(p.agreedPrice) }),
      }).catch(() => {});
    }
  };

  async function handleCalculate(options = {}) {
    // Defensive: handleCalculate é também usado em onClick directo de botões,
    // onde o primeiro argumento é um SyntheticEvent. Detectamos e ignoramos.
    const { avoidAreaBboxes = null, forceProvider = null } = (options && typeof options === 'object' && !options.nativeEvent) ? options : {};
    // Mercadoria: persiste os custos editados por ponto nas OTs respetivas.
    syncPointCostsToOTs();
    // Strip any empty/ghost waypoints from the state before calculating — belt-and-braces
    // against the "duplicate empty waypoint" bug. Iterate from the back so indices stay valid.
    {
      const pending = route.waypoints || [];
      for (let i = pending.length - 1; i >= 0; i--) {
        const w = pending[i];
        if (!w.coordinates && !w.name) {
          dispatch(removeWaypoint(i));
        }
      }
    }
    dispatch(setCalculating());
    // Logs de tempo do cálculo (F12) — para diagnosticar onde vão os segundos
    // (ex.: routing de camião HERE vs. rota de carro). Activado com
    // ?routecalc=1 no URL OU localStorage.routeCalcPerf='1'. Sem overhead quando off.
    const perfLog = (() => {
      try {
        return new URLSearchParams(window.location.search).get('routecalc') === '1'
          || window.localStorage.getItem('routeCalcPerf') === '1';
      } catch { return false; }
    })();
    const __tStart = performance.now();
    // Nome do provider escolhido — capturado fora do try para ficar disponível
    // no catch (log server-side do erro de cálculo — ver opção B).
    let calcProviderName = null;
    try {
      let waypoints = route.waypoints || [];
      let calcOrigin = route.origin;
      // Re-otimização de rota EM CURSO: se há paragens já entregues, não as
      // voltamos a incluir no cálculo nem partimos da origem original — o
      // motorista está a meio do percurso. Recalculamos a partir da posição
      // GPS actual do veículo (fallback: última paragem entregue → origem
      // original) e só com as paragens ainda por fazer. Persistimos origem +
      // waypoints (setOrigin/setWaypoints) para a geometria ficar alinhada
      // com o que é gravado e reenviado ao motorista ("Guardar e Reenviar").
      const deliveredWps = waypoints.filter(w => w.otStatus === 'delivered');
      if (deliveredWps.length > 0) {
        const remaining = waypoints.filter(w => w.coordinates && w.otStatus !== 'delivered');
        const gpsOrigin = getLiveVehicleOrigin();
        const lastDelivered = deliveredWps[deliveredWps.length - 1];
        const fallbackOrigin = lastDelivered?.coordinates
          ? { name: lang === 'pt' ? 'Última paragem concluída' : 'Last completed stop', coordinates: lastDelivered.coordinates }
          : route.origin;
        calcOrigin = gpsOrigin || fallbackOrigin;
        waypoints = remaining;
        dispatch(setOrigin(calcOrigin));
        dispatch(setWaypoints(remaining));
        dispatch(addToast({
          type: 'info',
          message: lang === 'pt'
            ? `Recálculo a partir ${gpsOrigin ? 'da posição actual do veículo' : 'da última paragem concluída'} — ${deliveredWps.length} entrega(s) já feita(s) excluída(s) do percurso.`
            : `Recomputed from ${gpsOrigin ? 'the current vehicle position' : 'the last completed stop'} — ${deliveredWps.length} completed delivery(ies) excluded.`,
        }));
      }
      const viaPoints = waypoints.filter(w => w.coordinates).map(w => w.coordinates);
      const isRoundTrip = route.roundTrip && calcOrigin?.coordinates && route.destination?.coordinates;
      const calcDestination = isRoundTrip ? calcOrigin.coordinates : route.destination.coordinates;
      const calcVias = isRoundTrip ? [...viaPoints, route.destination.coordinates] : viaPoints;

      // Alternativas: 3 para o modo "Comparar" (alimenta o diálogo
      // detalhado); 2 para o modo leve "Mostrar no mapa". Quando ambos
      // estão activos, 3 ganha. O provider devolve até esse número.
      const calcOpts = {
        ...opts,
        departureTime: route.departureTime || null,
        // País de operação do tenant — biases Google Routes (regionCode) e
        // popula countryDistances quando o provider não devolve spans (Google).
        regionCode: operationOrigin === 'ao' ? 'AO' : 'PT',
      };
      if (compareMode) calcOpts.alternatives = 3;
      else if (showAlternatives) calcOpts.alternatives = 2;
      // Avoid bboxes injectadas pelo botão "Recalcular evitando" — usado
      // quando o operador escolhe contornar uma zona com incidente crítico.
      // Só HERE suporta avoid de coords arbitrárias (Google não tem), por
      // isso o caller normalmente força HERE simultaneamente.
      if (Array.isArray(avoidAreaBboxes) && avoidAreaBboxes.length > 0) {
        calcOpts.avoidAreaBboxes = avoidAreaBboxes;
      }

      // Rota de assistência: detecta-se pela presença de waypointKind
      // 'service' em qualquer paragem ou no destino. Quando o provider
      // híbrido faz desempate (HERE = Google em distância ou duração)
      // preferimos Google nestas rotas — tem dados mais finos sobre
      // restrições urbanas em PT/ES, e a API já considera vias interditas
      // e incidentes em tempo real por defeito.
      const isAssistanceRoute = (waypoints || []).some(w => w.waypointKind === 'service')
        || route.destination?.waypointKind === 'service';
      if (isAssistanceRoute) calcOpts.preferGoogleOnTie = true;

      // Provider selection — for cars, the client can pick HERE (default),
      // Google (better urban restriction data) or 'hybrid' (runs both
      // and picks the safer one heuristically). Trucks always use HERE
      // because Google's truck profile lacks fine-grained PT/ES
      // attributes (axle weight, height tunnels, etc.).
      const isCar = opts.vehicleType === 'car';
      // Fornecedor por TIPO de veículo: ligeiros → carRoutingProvider,
      // pesados → truckRoutingProvider (ambos default HERE). Antes os pesados
      // estavam fixos em HERE; agora o operador pode escolher Google/Híbrido
      // também para camião/cisterna (útil em Angola, onde os atributos de
      // camião PT/ES do HERE não se aplicam).
      // Provider POR TIPO de veículo (override fino do perfil): se o subtipo
      // tiver provider próprio, ganha; senão HERDA dos dois baldes
      // (carRoutingProvider p/ carro, truckRoutingProvider p/ pesados).
      const currentSub = opts.vehicleSubType || (isCar ? 'car' : 'cisterna');
      const perTypeProvider = clientSettings?.routingProviderBySubType?.[currentSub];
      const configuredProvider = perTypeProvider
        || (isCar
          ? (clientSettings?.carRoutingProvider || 'here')
          : (clientSettings?.truckRoutingProvider || 'here'));
      // forceProvider sobrepõe a escolha do cliente — usado pelo botão
      // "Recalcular evitando" para garantir HERE (único que aceita avoid
      // de coordenadas arbitrárias).
      const effectiveProvider = forceProvider || configuredProvider;
      calcProviderName = effectiveProvider;
      const calcFn = effectiveProvider === 'google'
        ? calculateRouteGoogle
        : effectiveProvider === 'hybrid'
          ? calculateRouteHybrid
          : calculateRouteHERE;
      let result, alternatives, warning, providerUsed;
      try {
        // Quando o provider é Google, o resultado vem com countryDistances
        // num único bucket (limitação do fieldMask v2 — não devolve spans
        // por país). Fazemos uma chamada HERE em paralelo só para extrair
        // os spans correctos, mantendo a polyline + tráfego do Google.
        // Custa 1 chamada HERE extra mas é o que permite ter o card
        // "Combustível por País" correcto em rotas multi-país.
        // A chamada HERE extra (só para repartir a distância por país) é
        // DESPERDÍCIO em perfis MONO-PAÍS (ex.: Angola) — a distância é toda do
        // mesmo país, que o próprio Google já dá. Saltá-la poupa o tempo da
        // chamada HERE de camião (a mais lenta em rotas longas). Configurável no
        // perfil (backoffice → Rotas → "Perfil mono-país").
        const wantsHereEnrichment = calcFn === calculateRouteGoogle && !clientSettings?.singleCountry;
        const [computed, hereEnrichment] = await Promise.all([
          calcFn(calcOrigin.coordinates, calcDestination, calcVias, calcOpts),
          wantsHereEnrichment
            ? calculateRouteHERE(calcOrigin.coordinates, calcDestination, calcVias, calcOpts)
                .catch(err => {
                  // Falha silenciosa — não bloqueia a rota Google
                  console.warn('[Routing] HERE enrichment for countryDistances failed:', err?.message);
                  return null;
                })
            : Promise.resolve(null),
        ]);
        ({ route: result, alternatives, warning } = computed);
        if (hereEnrichment?.route?.countryDistances) {
          const cdHere = hereEnrichment.route.countryDistances;
          if (Object.keys(cdHere).length > 0) {
            result = { ...result, countryDistances: cdHere };
          }
        }
        // Provider efectivamente usado:
        //  · híbrido → o vencedor escolhido pelo pickBest (computed.hybridProvider)
        //  · fixo (HERE ou Google) → consoante a função chamada
        //  · trucks → sempre HERE (única alternativa hoje suportada)
        providerUsed = computed.hybridProvider
          || (calcFn === calculateRouteGoogle ? 'google' : 'here');
      } catch (gErr) {
        // If a non-HERE provider fails fall back to HERE so the user
        // still gets a route. A non-blocking warning is surfaced.
        if (calcFn !== calculateRouteHERE) {
          console.warn(`[Routing] ${configuredProvider} provider failed, falling back to HERE:`, gErr?.message);
          const computed = await calculateRouteHERE(
            calcOrigin.coordinates,
            calcDestination,
            calcVias,
            calcOpts
          );
          ({ route: result, alternatives, warning } = computed);
          providerUsed = 'here';
          warning = (warning ? warning + ' · ' : '') + `Provider ${configuredProvider} indisponível (${gErr?.message?.slice(0, 80) || 'erro'}). Foi usado HERE como alternativa.`;
        } else {
          throw gErr;
        }
      }
      if (perfLog) console.log(`[RouteCalc] routing(${calcProviderName}/${opts.vehicleSubType || opts.vehicleType}${(compareMode || showAlternatives) ? ' +alt' : ''}) = ${Math.round(performance.now() - __tStart)}ms`);
      const isLightVehicle = opts.vehicleType === 'car';
      // Gate alinhado com o separador "Paragens" do SidebarPanel — só
      // computamos stops quando o operador explicitamente quer (route
      // option === true). Além disso, a preferência do CLIENTE é autoritária:
      // se o cliente tem o tacógrafo desligado, o cálculo NÃO corre a cascata
      // de tacógrafo (Overpass + chamada ao tacógrafo + geocoding de paragens),
      // MESMO que a rota tenha chegado com useTachograph=true (ex.: default
      // hardcoded na criação, ou sync ainda não aplicado) — a menos que o
      // operador tenha ligado explicitamente para esta rota (tachoOverride).
      // Sem isto, uma rota fuel (cliente com tacho off) demorava ~5s por causa
      // de chamadas externas desnecessárias.
      const useTacho = !isLightVehicle
        && route.routeOptions?.useTachograph === true
        && (clientTachoPref === true || route.routeOptions?.tachoOverride === true);

      // Fetch real rest areas from Overpass (OSM) + favourites with includeInRoute flag
      let restAreasForTacho = [];
      const coords = result.geometry?.coordinates;
      if (useTacho && coords && coords.length > 1) {
        // Helper: calculate km along route for a point
        const sampled = coords.filter((_, i) => i % 10 === 0 || i === coords.length - 1);
        function calcKmAlongRoute(lat, lon) {
          const locCoord = [lon, lat];
          let minDist = Infinity;
          let bestIdx = 0;
          for (let i = 0; i < sampled.length; i++) {
            const d = haversineKm(locCoord, sampled[i]);
            if (d < minDist) { minDist = d; bestIdx = i; }
          }
          if (minDist > 15) return null; // Too far from route
          let kmAlong = 0;
          const actualIdx = Math.min(bestIdx * 10, coords.length - 1);
          for (let i = 1; i <= actualIdx; i++) {
            kmAlong += haversineKm(coords[i - 1], coords[i]);
          }
          return kmAlong;
        }

        // 1) Fetch REAL rest areas from Overpass API (await — blocking)
        try {
          const pois = await fetchPoisAlongRoute(coords);
          dispatch(setPois(pois));
          // Filter rest areas and parking from Overpass results
          const restTypes = new Set(['rest_area', 'services', 'truck_stop', 'parking']);
          const overpassRestAreas = (pois || [])
            .filter(p => restTypes.has(p.type) || p.amenity === 'parking')
            .map(p => {
              const km = p.distAlongRouteKm != null ? p.distAlongRouteKm : calcKmAlongRoute(p.lat, p.lon);
              if (km == null) return null;
              return {
                name: p.name || p.type || 'Área de descanso',
                lat: p.lat,
                lon: p.lon,
                distAlongRouteKm: km,
                preferred: false,
                source: 'overpass',
              };
            })
            .filter(Boolean);
          restAreasForTacho.push(...overpassRestAreas);
        } catch (e) {
          console.warn('Overpass POI fetch failed, using favourites only:', e.message);
        }

        // 2) Add FAVOURITES with includeInRoute flag only
        if (savedLocations && savedLocations.length > 0) {
          const routeTypes = new Set(['rest_area', 'overnight', 'fuel', 'safe_point']);
          const favouriteRestAreas = savedLocations
            .filter(loc => loc.includeInRoute && routeTypes.has(loc.type))
            .map(loc => {
              const km = calcKmAlongRoute(Number(loc.latitude), Number(loc.longitude));
              if (km == null) return null;
              return {
                name: loc.name,
                lat: Number(loc.latitude),
                lon: Number(loc.longitude),
                distAlongRouteKm: km,
                preferred: true, // favourites always preferred
                source: 'favourite',
              };
            })
            .filter(Boolean);
          restAreasForTacho.push(...favouriteRestAreas);
        }

        // Sort: favourites first, then by distance along route
        restAreasForTacho.sort((a, b) => {
          if (a.preferred && !b.preferred) return -1;
          if (!a.preferred && b.preferred) return 1;
          return (a.distAlongRouteKm || 0) - (b.distAlongRouteKm || 0);
        });
      }

      // Fetch driver's tachograph state if available
      let tachoData = null;
      if (useTacho && route.driverId && token) {
        try {
          const tachoRes = await fetch(`/planner-api/drivers/${route.driverId}/tacho`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (tachoRes.ok) tachoData = await tachoRes.json();
        } catch (e) { /* ignore — will use default limits */ }
      }

      const stops = useTacho
        ? calculateStops(result.duration, result.distance, route.departureTime, tachoData, restAreasForTacho)
        : [];

      // Enrich stops with city name via reverse geocoding (use rest area coords if snapped)
      if (coords && coords.length > 1 && stops.length > 0) {
        await Promise.all(stops.map(async stop => {
          if (stop.suggestedRestArea) {
            // Use the rest area's coordinates for geocoding
            stop.location = await reverseGeocodePlace(stop.suggestedRestArea.lon, stop.suggestedRestArea.lat);
          } else {
            const idx = Math.min(Math.round(stop.progressRatio * (coords.length - 1)), coords.length - 1);
            const [lng, lat] = coords[idx];
            stop.location = await reverseGeocodePlace(lng, lat);
          }
        }));
      }

      // ETA realista para PESADOS via Google: o Google Routes só tem modo
      // DRIVE (carro), logo devolve tempos otimistas para camião/cisterna.
      // Quando o provider FINAL é Google e o veículo é pesado, recalculamos a
      // duração a partir da velocidade média do perfil (distância ÷ velocidade)
      // e escalamos os troços na mesma proporção. HERE (perfil de camião) e os
      // ligeiros ficam INTACTOS. Cobre também o híbrido que escolheu Google.
      if (!isCar && providerUsed === 'google' && Number(result?.distance) > 0) {
        const avgKmh = Number(clientSettings?.defaultSpeed) > 0 ? Number(clientSettings.defaultSpeed) : 60;
        const truckDurationS = Math.round((Number(result.distance) / 1000) / avgKmh * 3600);
        const origDurationS = Number(result.duration) || 0;
        if (truckDurationS > 0 && origDurationS > 0) {
          const ratio = truckDurationS / origDurationS;
          result = {
            ...result,
            duration: truckDurationS,
            durationSource: 'truck-avg-speed',
            legs: Array.isArray(result.legs)
              ? result.legs.map(l => ({ ...l, duration: Math.round((Number(l.duration) || 0) * ratio) }))
              : result.legs,
          };
          warning = (warning ? warning + ' · ' : '')
            + (lang === 'pt'
              ? `Tempo estimado para pesado a ${avgKmh} km/h (o Google não modela camião).`
              : `Time estimated for a heavy vehicle at ${avgKmh} km/h (Google has no truck profile).`);
        }
      }

      dispatch(setRouteResult({ computedRoute: result, stops, alternatives, warning, provider: providerUsed }));
      if (perfLog) console.log(`[RouteCalc] rota VISÍVEL no mapa = ${Math.round(performance.now() - __tStart)}ms (total)`);
      dispatch(setShowDetails(true));

      // Fase 2 incidents-aware: depois de a rota estar pronta, varremos
      // o corredor de 500m à volta do trajecto à procura de incidentes
      // críticos (acidentes, vias cortadas, obras severas) reportados
      // por HERE+TomTom. Se houver, mostra-se um banner amarelo no
      // painel para o operador saber ANTES de despachar a rota.
      // Falha silenciosa — incidentes são informativos, nunca bloqueiam.
      //
      // GATE — só corre quando o provider NÃO é Google. Razões:
      //   • O Google já incorpora trânsito no cálculo e tipicamente já
      //     contornou o que era grave; HERE/TomTom marcariam tudo o que
      //     cai num corredor de 500 m (mesmo em estradas paralelas que
      //     a geometria não usa) → ruído / falsos positivos.
      //   • Quem escolhe Google confia na fonte Google. Avisos vindos
      //     de fontes concorrentes criam desconfiança ("afinal qual
      //     está certo?") sem oferecerem um loop coerente (não temos
      //     "avoid areas" do lado do Google).
      // Para HERE / Híbrido faz sentido: a rota foi calculada por HERE
      // e o aviso é da MESMA fonte → consistente, e o operador pode
      // recalcular evitando a zona.
      try {
        const coords = result?.geometry?.coordinates;
        const isGoogle = String(providerUsed || '').toLowerCase() === 'google';
        // Mono-país (ex.: Angola) → saltar o scan de incidentes. As fontes
        // HERE Traffic + TomTom têm cobertura ~nula fora da Europa, logo
        // cada cálculo faria uma chamada HERE Traffic (quota à parte do
        // routing) + TomTom sem valor nenhum. Poupa transações HERE quando
        // o provider de pesados é HERE num tenant mono-país.
        const skipIncidents = isGoogle || !!clientSettings?.singleCountry;
        if (skipIncidents) {
          setRouteIncidents([]); // limpa qualquer aviso de cálculos anteriores
        } else if (Array.isArray(coords) && coords.length > 1) {
          setRouteIncidents(null); // limpa anterior enquanto carrega
          validateRouteIncidents(coords).then(({ incidents }) => {
            setRouteIncidents(incidents || []);
          }).catch(() => setRouteIncidents([]));
        }
      } catch { /* não bloqueia o cálculo da rota */ }

      // Fetch toll data in background — only if european_tolls module is active
      const hasTolls = !modules || modules.length === 0 || modules.includes('european_tolls');
      if (hasTolls && token && calcOrigin?.coordinates && route.destination?.coordinates) {
        dispatch(fetchTollData({
          token,
          // calcOrigin/waypoints já refletem a re-otimização em curso (origem
          // GPS + paragens entregues excluídas) — portagens coerentes com o trajeto.
          origin: { lat: calcOrigin.coordinates[1], lng: calcOrigin.coordinates[0] },
          destination: { lat: route.destination.coordinates[1], lng: route.destination.coordinates[0] },
          waypoints,
          transportMode: opts.vehicleType === 'car' ? 'car' : 'truck',
          routeOptions: opts,
        }));
      }

      // Fetch fuel prices per country — if route crosses countries
      const cd = result.countryDistances;
      if (cd && Object.keys(cd).length > 0 && token) {
        // NÃO apagamos aqui os overrides à mão: o operador que define um preço
        // pontual no painel direito quer que ele PERSISTA ao recalcular (fica
        // para ser gravado com a rota). Overrides de países que saiam da rota
        // ficam inertes. A limpeza faz-se só ao "Limpar Rota" (rota nova).
        const fuelPricesPromise = dispatch(fetchFuelPrices({
          token,
          countries: Object.keys(cd),
          fuelType: route.fuelType || 'diesel',
        }));
        // Só o modo COMPARAR precisa dos preços resolvidos de imediato (lê-os
        // do store logo a seguir). Numa rota simples NÃO bloqueamos: a rota já
        // está no mapa (setRouteResult acima) e o painel de custos actualiza-se
        // reactivamente quando os preços chegam — handleCalculate termina antes.
        if (compareMode) {
          await fuelPricesPromise;
        }
        if (perfLog) console.log(`[RouteCalc] preços combustível lançados (+${Math.round(performance.now() - __tStart)}ms do início)`);
      }

      // If compare mode, build comparison data and open dialog
      if (compareMode && alternatives && alternatives.length > 0) {
        const ROUTE_COLORS = ['#3b82f6', '#22c55e', '#f59e0b', '#8b5cf6'];
        const allRoutes = [result, ...alternatives];
        // Get fresh fuel prices from store (after await fetchFuelPrices)
        const freshState = store.getState().fuelPrices;
        const freshPrices = freshState?.prices || {};
        const freshOverrides = freshState?.overrides || {};
        const compareData = allRoutes.map((r, i) => ({
          distance: r.distance,
          duration: r.duration,
          geometry: r.geometry,
          roadBreakdown: r.roadBreakdown,
          countryDistances: r.countryDistances,
          fuelCost: calcFuelCost(r, freshPrices, freshOverrides),
          tollCost: 0,
          totalCost: calcFuelCost(r, freshPrices, freshOverrides),
          label: '',
          color: ROUTE_COLORS[i] || '#94a3b8',
        }));

        // Determine labels: fastest, shortest, cheapest (by fuel for now)
        let fastestIdx = 0, shortestIdx = 0, cheapestIdx = 0;
        compareData.forEach((r, i) => {
          if (r.duration < compareData[fastestIdx].duration) fastestIdx = i;
          if (r.distance < compareData[shortestIdx].distance) shortestIdx = i;
          if (r.fuelCost < compareData[cheapestIdx].fuelCost) cheapestIdx = i;
        });

        const ptLabels = ['Rota Principal', 'Alternativa 1', 'Alternativa 2', 'Alternativa 3'];
        const enLabels = ['Main Route', 'Alternative 1', 'Alternative 2', 'Alternative 3'];
        compareData.forEach((r, i) => {
          r.label = lang === 'pt' ? ptLabels[i] : enLabels[i];
        });

        // Best route scoring: 40% cost, 30% time, 20% distance, 10% fuel efficiency
        const maxDur = Math.max(...compareData.map(r => r.duration));
        const maxDist = Math.max(...compareData.map(r => r.distance));
        const maxFuel = Math.max(...compareData.map(r => r.fuelCost));
        let bestIdx = 0, bestScore = Infinity;
        compareData.forEach((r, i) => {
          const score = (r.fuelCost / maxFuel) * 0.4 + (r.duration / maxDur) * 0.3 + (r.distance / maxDist) * 0.2 + (r.fuelCost / maxFuel) * 0.1;
          if (score < bestScore) { bestScore = score; bestIdx = i; }
        });

        setBestRouteIdx(bestIdx);
        compareAllRoutesRef.current = allRoutes;
        setCompareRoutes(compareData);
        setSelectedCompareIdx(0); // a rota aplicada ao abrir é a principal (índice 0)
        setCompareOpen(true);
        dispatch(setDetailsPanelOpen(false));
        setCompareLoading(true);

        // Fetch tolls for each route in parallel (background)
        if (hasTolls && token) {
          try {
            const tollPromises = allRoutes.map(async (r) => {
              try {
                const coords = r.geometry?.coordinates;
                if (!coords || coords.length < 2) return null;
                const origin = { lat: coords[0][1], lng: coords[0][0] };
                const dest = { lat: coords[coords.length - 1][1], lng: coords[coords.length - 1][0] };
                const res = await fetch(`/planner-api/tolls/route?originLat=${origin.lat}&originLng=${origin.lng}&destLat=${dest.lat}&destLng=${dest.lng}&transportMode=${opts.vehicleType === 'car' ? 'car' : 'truck'}`, {
                  headers: { Authorization: `Bearer ${token}` },
                });
                if (!res.ok) return null;
                const data = await res.json();
                return data.totalCost || 0;
              } catch { return null; }
            });
            const tollResults = await Promise.all(tollPromises);
            setCompareRoutes(prev => prev.map((r, i) => ({
              ...r,
              tollCost: tollResults[i] ?? 0,
              totalCost: r.fuelCost + (tollResults[i] ?? 0),
            })));

            // Recalculate best with toll data
            setCompareRoutes(prev => {
              const updated = [...prev];
              const maxTotal = Math.max(...updated.map(r => r.totalCost || r.fuelCost));
              const maxD = Math.max(...updated.map(r => r.duration));
              const maxDi = Math.max(...updated.map(r => r.distance));
              let bIdx = 0, bScore = Infinity;
              updated.forEach((r, i) => {
                const total = r.totalCost || r.fuelCost;
                const score = (total / maxTotal) * 0.4 + (r.duration / maxD) * 0.3 + (r.distance / maxDi) * 0.2 + (r.fuelCost / maxTotal) * 0.1;
                if (score < bScore) { bScore = score; bIdx = i; }
              });
              setBestRouteIdx(bIdx);
              return updated;
            });
          } catch {
            setCompareRoutes(prev => prev.map(r => ({ ...r, tollCost: 0, totalCost: r.fuelCost })));
          }
          setCompareLoading(false);
        } else {
          setCompareRoutes(prev => prev.map(r => ({ ...r, tollCost: 0, totalCost: r.fuelCost })));
          setCompareLoading(false);
        }
      }
    } catch (err) {
      // Enriquece o erro com as coordenadas da origem/destino + nome do
      // provider, para o operador perceber RAPIDAMENTE se foi: (a) coord
      // inválida (off-road, mar), (b) zona sem dados, (c) bug do provider.
      const o = route.origin?.coordinates;
      const d = route.destination?.coordinates;
      const fmt = (c) => Array.isArray(c) && c.length === 2 ? `${c[1].toFixed(5)}, ${c[0].toFixed(5)}` : 'sem coordenadas';
      const detail = `Origem: ${fmt(o)} · Destino: ${fmt(d)}`;
      const baseMsg = err.message || 'Erro ao calcular rota';
      console.error('[RouteForm] calc failed:', baseMsg, { origin: o, destination: d, error: err });
      dispatch(setRouteError(`${baseMsg} — ${detail}`));
      // Opção B — persiste o motivo no servidor (fica no histórico da OT quando
      // há OT associada). Assim, se voltar a acontecer uma "rota fantasma sem
      // causa", temos o registo para diagnosticar. Fire-and-forget: nunca
      // bloqueia nem quebra o cálculo se o log falhar.
      try {
        const otId = route.destination?.otId
          || (route.waypoints || []).map(w => w.otId).find(Boolean)
          || route.sourceOTId
          || (Array.isArray(route.sourceOTIds) ? route.sourceOTIds[0] : null)
          || null;
        const otNumber = route.destination?.orderNumber
          || (route.waypoints || []).map(w => w.orderNumber).find(Boolean)
          || route.cargoRef || null;
        if (token) {
          fetch('/planner-api/routes/log-calc-error', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({
              orderId: otId, orderNumber: otNumber,
              provider: calcProviderName,
              origin: fmt(o), destination: fmt(d),
              message: String(baseMsg).slice(0, 500),
            }),
          }).catch(() => { /* best-effort */ });
        }
      } catch { /* nunca quebra o cálculo */ }
    }
  }

  // Store all computed routes for compare selection
  const compareAllRoutesRef = useRef([]);

  function handleMapPick(mode) {
    dispatch(setMapClickMode(mapClickMode === mode ? null : mode));
  }

  async function handleFetchOTs() {
    if (showOTPicker) { setShowOTPicker(false); return; }
    setShowOTPicker(true);
    setOtLoading(true);
    try {
      const res = await fetch('/planner-api/transport-orders?status=pending&limit=50', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setPendingOTs(data.data || []);
    } catch (err) {
      console.error('Fetch OTs error:', err);
      setPendingOTs([]);
    } finally {
      setOtLoading(false);
    }
  }

  function handleImportOT(ot) {
    const pickup = { name: ot.pickupName || ot.pickupAddress, coordinates: [Number(ot.pickupLng), Number(ot.pickupLat)] };
    // OT fuel (Move Express) sem delivery: o PA é revelado só no
    // carregamento (Ordem Sonangol). Rota inicial vai até ao terminal de
    // carga — quando o operador preencher o PA depois, recalcula-se.
    const hasDeliveryCoords = ot.deliveryLat != null && ot.deliveryLng != null;
    const delivery = hasDeliveryCoords
      ? { name: ot.deliveryName || ot.deliveryAddress, coordinates: [Number(ot.deliveryLng), Number(ot.deliveryLat)] }
      : null;
    const hasOrigin = route.origin?.coordinates;
    const hasDestination = route.destination?.coordinates;

    if (!hasOrigin) {
      dispatch(setOrigin(pickup));
      // Sem delivery → pickup também é o destino (rota single-leg).
      dispatch(setDestination(delivery || pickup));
    } else if (!hasDestination) {
      if (delivery) {
        dispatch(addWaypoint(pickup));
        dispatch(setDestination(delivery));
      } else {
        dispatch(setDestination(pickup));
      }
    } else {
      dispatch(addWaypoint(pickup));
      if (delivery) dispatch(addWaypoint(delivery));
    }

    if (ot.orderNumber) {
      dispatch(setCargoRef(ot.orderNumber));
    }
    dispatch(setNeedsRecalculation(true));
    setShowOTPicker(false);
  }

  function handleAddWaypoint() {
    // Guard: if there's already a pending empty waypoint at the tail, don't add
    // another one — this fixes the "ghost" duplicate empty row that appeared
    // after calculating a route with an intermediate point.
    const current = route.waypoints || [];
    const last = current[current.length - 1];
    if (last && !last.coordinates && !last.name) return;
    dispatch(addWaypoint({ name: '', coordinates: null }));
  }

  async function handleSaveToLocations(locData) {
    if (!token) return;
    const result = await dispatch(createLocation({ token, location: locData }));
    if (result.error) {
      console.error('Save location error:', result.error);
    }
  }

  const isReadOnly = !!route.readOnly;

  return (
    <Box className={classes.root} style={isReadOnly ? { position: 'relative' } : undefined}>
      {/* Read-only mode banner — shown when viewing a route from OT context */}
      {isReadOnly && (
        <Box style={{
          padding: '8px 14px',
          background: 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)',
          border: '1px solid #c7d2fe',
          borderRadius: 10, marginBottom: 8,
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <Box style={{
            width: 24, height: 24, borderRadius: 6,
            background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="#fff"><path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/></svg>
          </Box>
          <Box>
            <Typography style={{ fontSize: '0.72rem', fontWeight: 800, color: '#4338ca' }}>
              {lang === 'pt' ? 'Modo consulta' : 'View mode'}
            </Typography>
            <Typography style={{ fontSize: '0.58rem', color: '#6366f1' }}>
              {lang === 'pt' ? 'Visualização da rota — edição não disponível' : 'Route viewing — editing not available'}
            </Typography>
          </Box>
        </Box>
      )}
      {/* When read-only, overlay all interactive sections to block edits */}
      {isReadOnly && (
        <Box style={{
          position: 'absolute', top: 68, left: 0, right: 0, bottom: 0,
          zIndex: 5, pointerEvents: 'none',
        }}>
          <Box style={{
            position: 'absolute', inset: 0,
            background: 'rgba(255,255,255,0.35)',
            pointerEvents: 'all',
            cursor: 'not-allowed',
          }} onClick={(e) => e.stopPropagation()} />
        </Box>
      )}
      {/* ═══ Section 1: Rota ═══ */}
      <CollapsibleSection
        title="Rota"
        icon={<DirectionsIcon style={{ fontSize: 14, color: '#22c55e' }} />}
        iconColor="#22c55e"
        iconBg="#f0fdf4"
        open={openSections.route}
        onToggle={() => toggleSection('route')}
      >
      <Box style={{ marginTop: 4 }}>
        {/* ROTA header: "Rota Origem / Destino" left + round trip checkbox right */}
        <Box style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
          <Typography component="span" style={{ fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.06em' }}>
            <span style={{ color: '#22c55e' }}>Origem</span>
            <span style={{ color: '#64748b' }}> / </span>
            <span style={{ color: '#ef4444' }}>Destino</span>
          </Typography>
          {/* Compact info badge: shows the warning that used to live in
              its own yellow box at the bottom of the panel. Hover or
              tap to read the full message. The amber pulse hints at
              "there's something to read" without being intrusive. */}
          {route.warning && !(route.warning.startsWith('Híbrido') && route.provider) && (
            <Tooltip
              title={<span style={{ whiteSpace: 'pre-line' }}>{route.warning}</span>}
              placement="bottom-start"
              arrow
              enterDelay={150}
            >
              <Box style={{
                marginLeft: 6,
                width: 16, height: 16,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
                color: '#fff',
                fontSize: '0.6rem',
                fontWeight: 800,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'help',
                boxShadow: '0 0 0 2px rgba(251,191,36,0.18)',
                animation: 'routeInfoPulse 2.4s ease-in-out infinite',
              }}>i</Box>
            </Tooltip>
          )}
          {/* Badge de incidentes críticos sobre a rota — vermelho se >0,
              cinza pulsante enquanto a validação está a correr.
              Click expande/recolhe a lista detalhada por baixo. */}
          {Array.isArray(routeIncidents) && routeIncidents.length > 0 && (
            <Tooltip
              title={`${routeIncidents.length} incidente(s) crítico(s) detectado(s) sobre a rota — clique para ver`}
              placement="bottom-start"
              arrow
              enterDelay={150}
            >
              <Box
                onClick={() => setRouteIncidentsExpanded(v => !v)}
                style={{
                  marginLeft: 4,
                  height: 18,
                  padding: '0 8px',
                  borderRadius: 999,
                  background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                  color: '#fff',
                  fontSize: '0.62rem',
                  fontWeight: 800,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 3,
                  cursor: 'pointer',
                  boxShadow: '0 0 0 2px rgba(220,38,38,0.18)',
                  animation: 'routeInfoPulse 2.4s ease-in-out infinite',
                  letterSpacing: '0.04em',
                }}
              >
                ⚠ {routeIncidents.length} {routeIncidents.length === 1 ? 'incidente' : 'incidentes'}
              </Box>
            </Tooltip>
          )}
          {/* Badge do provider — mostra qual o motor de routing efectivamente
              usado no cálculo. Tooltip explica a escolha (fixo HERE/Google,
              ou desempate do híbrido). Clicável para abrir/fechar a tooltip
              em ecrãs touch. */}
          {route.status === 'ready' && route.provider && (
            <Tooltip
              placement="bottom-start"
              arrow
              enterDelay={150}
              title={
                <Box style={{ padding: '4px 2px', maxWidth: 340 }}>
                  <Typography style={{ fontSize: '0.78rem', fontWeight: 800, marginBottom: 4, color: '#fff' }}>
                    {route.provider === 'here' ? 'HERE Maps' : route.provider === 'google' ? 'Google Routes' : route.provider}
                  </Typography>
                  <Typography style={{ fontSize: '0.7rem', lineHeight: 1.5, color: '#fff' }}>
                    {lang === 'pt' ? (
                      <>
                        Motor de routing usado para calcular esta rota.
                        <Box style={{ marginTop: 6 }}>
                          {route.provider === 'here'
                            ? <>Forte em <b>camiões</b> (peso/altura/eixos), redes europeias e atributos de estradas detalhados. Default para HGV.</>
                            : <>Forte em <b>restrições urbanas</b> em PT/ES (one-ways, zonas pedonais) e tráfego real-time. Default para carros quando o cliente prefere Google ou empate em rotas de assistência.</>}
                        </Box>
                        <Box style={{ marginTop: 6, fontStyle: 'italic', opacity: 0.85 }}>
                          A escolha pode mudar consoante o tipo de veículo, configuração do cliente, ou desempate do provider híbrido.
                        </Box>
                      </>
                    ) : (
                      <>
                        Routing engine used to compute this route.
                        <Box style={{ marginTop: 6 }}>
                          {route.provider === 'here'
                            ? <>Strong on <b>trucks</b> (weight/height/axles), European networks and detailed road attributes. Default for HGV.</>
                            : <>Strong on <b>urban restrictions</b> in PT/ES (one-ways, pedestrian zones) and real-time traffic. Default for cars when the client prefers Google or as tie-breaker for assistance routes.</>}
                        </Box>
                        <Box style={{ marginTop: 6, fontStyle: 'italic', opacity: 0.85 }}>
                          The choice depends on vehicle type, client config, or hybrid tie-breaker.
                        </Box>
                      </>
                    )}
                  </Typography>
                  {/* Razão da decisão do híbrido (se houver warning desse tipo) —
                      mostra ao operador porque é que o pickBest escolheu este
                      provider em detrimento do outro. */}
                  {route.warning && route.warning.startsWith('Híbrido') && (
                    <Box style={{
                      marginTop: 8, paddingTop: 8,
                      borderTop: '1px solid rgba(255,255,255,0.18)',
                    }}>
                      <Typography style={{ fontSize: '0.66rem', color: '#fde68a', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 2 }}>
                        {lang === 'pt' ? 'Decisão do híbrido' : 'Hybrid decision'}
                      </Typography>
                      <Typography style={{ fontSize: '0.66rem', lineHeight: 1.45, color: '#fff', whiteSpace: 'pre-line' }}>
                        {route.warning}
                      </Typography>
                    </Box>
                  )}
                </Box>
              }
            >
              <Box style={{
                marginLeft: 4,
                width: 14, height: 14,
                borderRadius: '50%',
                background: '#f8fafc',
                color: route.provider === 'google' ? '#1d4ed8' : '#92400e',
                fontSize: '0.58rem',
                fontWeight: 800,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'help',
                border: `1px solid ${route.provider === 'google' ? '#bfdbfe' : '#fde68a'}`,
                lineHeight: 1,
                fontFamily: '"SF Mono","Monaco","Courier New",monospace',
              }}>
                {route.provider === 'google' ? 'G' : 'H'}
              </Box>
            </Tooltip>
          )}
          <Box style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}>
            {/* Round trip toggle — escondido em rotas vindas de OTs.
                Para OT-derived a origem/destino são determinados pelos
                serviços e (opcionalmente) regresso a casa, então
                "ida e volta" não tem semântica clara e confundia o
                operador. Só visível em rotas criadas manualmente
                (sem sourceOTId/sourceOTIds). */}
            {(() => {
              const isOTDerived = !!(route.sourceOTId
                || (Array.isArray(route.sourceOTIds) && route.sourceOTIds.length > 0));
              if (isOTDerived) return null;
              return (
                <Box
                  onClick={() => dispatch(setRoundTrip(!route.roundTrip))}
                  style={{ display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer', userSelect: 'none' }}
                >
                  <Checkbox
                    checked={route.roundTrip || false}
                    size="small"
                    style={{ padding: 1, color: route.roundTrip ? '#3b82f6' : '#cbd5e1' }}
                  />
                  <SwapHorizIcon style={{ fontSize: 12, color: route.roundTrip ? '#3b82f6' : '#94a3b8' }} />
                  <Typography style={{ fontSize: '0.58rem', fontWeight: 600, color: route.roundTrip ? '#3b82f6' : '#94a3b8' }}>
                    {t(lang, 'roundTrip')}
                  </Typography>
                </Box>
              );
            })()}
          </Box>
        </Box>

        {/* Painel expansível de incidentes críticos sobre a rota.
            Aparece quando o badge vermelho do header é clicado. Lista
            cada incidente com tipo, severidade, distância à rota e
            fonte (HERE/TomTom). NÃO faz re-routing automático — só
            informa o operador para que ele decida. */}
        {Array.isArray(routeIncidents) && routeIncidents.length > 0 && routeIncidentsExpanded && (
          <Box style={{
            margin: '6px 0',
            padding: '10px 12px',
            background: 'linear-gradient(180deg, #fef2f2 0%, #fff 100%)',
            border: '1.5px solid #fecaca',
            borderRadius: 10,
          }}>
            <Box style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
              <Typography style={{ fontSize: '0.72rem', fontWeight: 800, color: '#991b1b', flex: 1 }}>
                ⚠ A rota atravessa {routeIncidents.length} {routeIncidents.length === 1 ? 'incidente crítico' : 'incidentes críticos'}
              </Typography>
              <Box
                onClick={() => setRouteIncidentsExpanded(false)}
                style={{
                  cursor: 'pointer', fontSize: '0.62rem', color: '#64748b',
                  fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em',
                  padding: '2px 8px', borderRadius: 6,
                  border: '1px solid #e2e8f0', background: '#fff',
                }}
              >
                Fechar
              </Box>
            </Box>
            <Box style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {routeIncidents.map((inc, idx) => {
                const typeName = inc.typeName || {};
                const sourceCfg = inc.source === 'tomtom'
                  ? { label: 'TomTom', color: '#fff', bg: '#dc2626' }
                  : { label: 'HERE', color: '#fff', bg: '#0ea5e9' };
                return (
                  <Box key={`${inc.id}-${idx}`} style={{
                    display: 'flex', alignItems: 'flex-start', gap: 8,
                    padding: '6px 8px', background: '#fff',
                    border: '1px solid #f1f5f9', borderRadius: 8,
                    borderLeft: `3px solid ${typeName.color || '#dc2626'}`,
                  }}>
                    <span style={{ fontSize: 16, flexShrink: 0 }}>{typeName.emoji || '⚠'}</span>
                    <Box style={{ flex: 1, minWidth: 0 }}>
                      <Box style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap', marginBottom: 2 }}>
                        <Typography style={{ fontSize: '0.7rem', fontWeight: 800, color: '#0f172a' }}>
                          {typeName.label || 'Incidente'}
                        </Typography>
                        {inc.roadClosed && (
                          <span style={{ fontSize: 8, fontWeight: 800, color: '#fff', background: '#dc2626', padding: '1px 5px', borderRadius: 3, letterSpacing: '0.04em' }}>
                            VIA FECHADA
                          </span>
                        )}
                        <span style={{ fontSize: 8, fontWeight: 800, color: sourceCfg.color, background: sourceCfg.bg, padding: '1px 5px', borderRadius: 3, letterSpacing: '0.04em' }}>
                          {sourceCfg.label}
                        </span>
                        {inc.alsoReportedBy && (
                          <span style={{ fontSize: 8, fontWeight: 700, color: '#16a34a', background: '#dcfce7', padding: '1px 5px', borderRadius: 3 }}>
                            ✓ {inc.alsoReportedBy.toUpperCase()}
                          </span>
                        )}
                      </Box>
                      <Typography style={{ fontSize: '0.66rem', color: '#475569', lineHeight: 1.4 }}>
                        {inc.summary || inc.description || ''}
                      </Typography>
                      <Typography style={{ fontSize: '0.58rem', color: '#94a3b8', marginTop: 2 }}>
                        a ~{inc._routeDistanceM} m do trajecto
                        {inc.criticality && ` · severidade ${({ critical: 'crítica', major: 'alta', minor: 'baixa', lowImpact: 'baixa' })[inc.criticality] || inc.criticality}`}
                        {inc.endTime && ` · termina ${new Date(inc.endTime).toLocaleString('pt-PT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}`}
                      </Typography>
                      {/* Botão "Recalcular evitando" — constrói bbox ~600m
                          à volta do incidente e força HERE (único provider
                          que aceita avoid[areas] arbitrários). */}
                      <Box style={{ marginTop: 4 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            const [lng, lat] = inc.coordinates || [];
                            if (!Number.isFinite(lng) || !Number.isFinite(lat)) return;
                            // 600m ≈ 0.0054° lat; lng varia com latitude
                            const dLat = 600 / 111320;
                            const dLng = 600 / (111320 * Math.cos(lat * Math.PI / 180));
                            const bbox = {
                              west: lng - dLng,
                              south: lat - dLat,
                              east: lng + dLng,
                              north: lat + dLat,
                            };
                            handleCalculate({ avoidAreaBboxes: [bbox], forceProvider: 'here' });
                          }}
                          style={{
                            fontSize: '0.62rem', fontWeight: 700, textTransform: 'none',
                            padding: '2px 8px', minHeight: 22,
                            borderColor: '#fcd34d', color: '#92400e',
                            background: '#fffbeb',
                          }}>
                          ↻ {lang === 'pt' ? 'Recalcular evitando esta zona' : 'Recalculate avoiding this area'}
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
            <Typography style={{ fontSize: '0.58rem', color: '#94a3b8', marginTop: 8, fontStyle: 'italic' }}>
              💡 Clica "Recalcular evitando" para contornar o incidente, ou prossegue se preferires manter o trajecto actual.
            </Typography>
          </Box>
        )}

        {/* Hidden file input for document uploads */}
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: 'none' }}
          accept=".pdf,.jpg,.jpeg,.png"
          onChange={handleFileInputChange}
        />

        {/* Origin */}
        <Box style={{ position: 'relative' }}>
          <GeocoderInput
            label={t(lang, 'origin')}
            value={route.origin?.name || ''}
            lang={lang}
            icon={MyLocationIcon}
            iconColor="#22c55e"
            coordinates={route.origin?.coordinates}
            plateTooltip={route.origin?.vehiclePlate ? (lang === 'pt' ? `Veículo ${route.origin.vehiclePlate}` : `Vehicle ${route.origin.vehiclePlate}`) : undefined}
            onSelect={loc => {
              dispatch(setOrigin({ name: loc.name, coordinates: loc.coordinates }));
              if (loc.coordinates) {
                window.dispatchEvent(new CustomEvent('mapPanTo', {
                  detail: { lat: loc.coordinates[1], lng: loc.coordinates[0] },
                }));
              }
            }}
            onMapPick={() => handleMapPick('origin')}
            onSave={handleSaveToLocations}
            savedLocations={savedLocations}
            showFavoritePicker
          />
          {/* Document chips for origin (waypointIndex = -1) — attach icon removed */}
          <Box style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 28, marginTop: 2, flexWrap: 'wrap' }}>
            {documents.filter(d => d.waypointIndex === -1).map(doc => (
              <Chip
                key={doc.id}
                size="small"
                icon={<DescriptionIcon style={{ fontSize: 12 }} />}
                label={doc.originalName.length > 18 ? doc.originalName.slice(0, 15) + '...' : doc.originalName}
                onClick={() => window.open(doc.url, '_blank')}
                onDelete={() => handleDocDelete(doc.id)}
                style={{ height: 20, fontSize: '0.6rem', maxWidth: 160 }}
                title={doc.originalName}
              />
            ))}
          </Box>
        </Box>
        {mapClickMode === 'origin' && (
          <Typography style={{ fontSize: '0.65rem', color: '#3b82f6', marginLeft: 36, marginTop: 2 }}>
            {t(lang, 'clickMapOrigin')}
          </Typography>
        )}

        <Box className={classes.connector} />

        {/* Waypoints — inline in left panel with drag-and-drop reordering.
            The outer Box is a DROP TARGET only; drag starts on the dedicated
            handle icon so text inputs and the numbered circle stay fully interactive. */}
        {(route.waypoints || []).map((wp, idx) => (
          <React.Fragment key={`wp-${idx}-${wp.name || ''}`}>
            <Box
              onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOverIdx(idx); }}
              onDragLeave={() => setDragOverIdx(null)}
              onDrop={(e) => {
                e.preventDefault();
                if (dragIdx !== null && dragIdx !== idx) {
                  dispatch(reorderWaypoints({ fromIndex: dragIdx, toIndex: idx }));
                  dispatch(setNeedsRecalculation(true));
                }
                setDragIdx(null);
                setDragOverIdx(null);
              }}
              onDragEnd={() => { setDragIdx(null); setDragOverIdx(null); }}
              style={{
                position: 'relative',
                opacity: dragIdx === idx ? 0.4 : 1,
                background: dragOverIdx === idx && dragIdx !== idx ? 'rgba(99,102,241,0.08)' : 'transparent',
                borderTop: dragOverIdx === idx && dragIdx !== idx ? '2px solid #6366f1' : '2px solid transparent',
                transition: 'opacity 0.15s, background 0.15s',
              }}
            >
              {/* Drag handle icon — the ONLY draggable element in the row.
                  Positioned vertically stacked with the number badge so the row
                  layout stays identical to the non-draggable siblings. */}
              <Tooltip title={lang === 'pt' ? 'Arrastar para reordenar paragem' : 'Drag to reorder stop'}>
                <Box
                  draggable
                  onDragStart={(e) => {
                    setDragIdx(idx);
                    e.dataTransfer.effectAllowed = 'move';
                    try { e.dataTransfer.setData('text/plain', String(idx)); } catch {}
                  }}
                  onDragEnd={() => { setDragIdx(null); setDragOverIdx(null); }}
                  style={{
                    position: 'absolute', left: 2, top: 30,
                    width: 18, height: 18,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'grab', color: '#94a3b8', zIndex: 2,
                    borderRadius: 4,
                    transition: 'color 0.15s, background 0.15s',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = '#6366f1'; e.currentTarget.style.background = '#eef2ff'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = '#94a3b8'; e.currentTarget.style.background = 'transparent'; }}
                >
                  <DragIndicatorIcon style={{ fontSize: 14 }} />
                </Box>
              </Tooltip>
              {/* Number badge — colour reflects waypointKind */}
              <Box style={{ position: 'absolute', left: 2, top: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, zIndex: 1 }}>
                <Tooltip title={
                  wp.waypointKind === 'pickup' ? (lang === 'pt' ? 'Recolha — clique para mudar tipo' : 'Pickup — click to change type')
                  : wp.waypointKind === 'delivery' ? (lang === 'pt' ? 'Entrega — clique para mudar tipo' : 'Delivery — click to change type')
                  : wp.waypointKind === 'fuel' ? (lang === 'pt' ? 'Abastecimento — clique para mudar tipo' : 'Fuel — click to change type')
                  : wp.waypointKind === 'rest' ? (lang === 'pt' ? 'Pausa — clique para mudar tipo' : 'Rest — click to change type')
                  : (lang === 'pt' ? 'Clique para definir tipo' : 'Click to set type')
                }>
                  <Box
                    onClick={(e) => {
                      e.stopPropagation();
                      const order = ['delivery', 'pickup', 'service', 'fuel', 'rest', null];
                      const cur = wp.waypointKind || null;
                      const nextIdx = (order.indexOf(cur) + 1) % order.length;
                      dispatch(updateWaypoint({ index: idx, waypoint: { ...wp, waypointKind: order[nextIdx] } }));
                    }}
                    style={{
                      width: 18, height: 18, borderRadius: '50%', color: '#fff',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '0.58rem', fontWeight: 700, cursor: 'pointer',
                      // OT já entregue → verde, sobrepõe-se ao tipo de paragem.
                      background: wp.otStatus === 'delivered' ? '#16a34a'
                        : wp.waypointKind === 'pickup' ? '#22c55e'
                        : wp.waypointKind === 'delivery' ? '#3b82f6'
                        : wp.waypointKind === 'service' ? '#7c3aed'
                        : wp.waypointKind === 'fuel' ? '#ef4444'
                        : wp.waypointKind === 'rest' ? '#8b5cf6'
                        : '#f59e0b',
                    }}>
                    {wp.otStatus === 'delivered' ? '✓' : idx + 1}
                  </Box>
                </Tooltip>
              </Box>
              <GeocoderInput
                label={`${t(lang, 'waypoint')} ${idx + 1}`}
                value={wp.name || ''}
                lang={lang}
                icon={RadioButtonUncheckedIcon}
                iconColor="#f59e0b"
                coordinates={wp.coordinates}
                onSelect={loc => dispatch(updateWaypoint({ index: idx, waypoint: { ...wp, name: loc.name, coordinates: loc.coordinates } }))}
                onMapPick={() => handleMapPick(`waypoint-${idx}`)}
                onRemove={() => dispatch(removeWaypoint(idx))}
                onSave={handleSaveToLocations}
                savedLocations={savedLocations}
                showFavoritePicker
              />
              {/* OT context + price chips + stop duration (editable inline).
                  Stop duration is persisted in the route's waypoints JSON field. */}
              <Box style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 28, marginTop: 3, flexWrap: 'wrap' }}>
                {/* Stop duration input — small number field with "min" adornment */}
                <Tooltip title={lang === 'pt' ? 'Tempo de paragem (minutos)' : 'Stop duration (minutes)'}>
                  <Box style={{ display: 'flex', alignItems: 'center', gap: 3, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 4, padding: '1px 5px', height: 18 }}>
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="#94a3b8">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.2 14.2L11 13V7h1.5v5.2l4.5 2.7-.8 1.3z"/>
                    </svg>
                    <input
                      type="number"
                      min="0"
                      step="5"
                      value={wp.stopDuration ?? ''}
                      placeholder={wp.waypointKind === 'pickup' ? '15' : wp.waypointKind === 'delivery' ? '30' : '0'}
                      onChange={(e) => {
                        const v = e.target.value === '' ? null : Math.max(0, parseInt(e.target.value) || 0);
                        dispatch(updateWaypoint({ index: idx, waypoint: { ...wp, stopDuration: v } }));
                      }}
                      style={{ width: 28, border: 'none', outline: 'none', background: 'transparent', fontSize: '0.6rem', color: '#1e293b', fontWeight: 700, textAlign: 'right' }}
                    />
                    <Typography style={{ fontSize: '0.54rem', color: '#94a3b8', fontWeight: 600 }}>min</Typography>
                  </Box>
                </Tooltip>
                {/* Custo da entrega editável — mercadoria (Casais) + ponto de OT.
                    Sincroniza na OT respetiva ao calcular/recalcular. */}
                {isMercadoria && wp.otId && (
                  <Tooltip title={lang === 'pt' ? 'Custo da entrega (€) — sincroniza na OT ao recalcular' : 'Delivery cost (€) — synced to OT on recalc'}>
                    <Box style={{ display: 'flex', alignItems: 'center', gap: 3, background: '#fff', border: '1px solid #fde68a', borderRadius: 4, padding: '1px 5px', height: 18 }}>
                      <Typography style={{ fontSize: '0.56rem', color: '#92400e', fontWeight: 800 }}>€</Typography>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={wp.agreedPrice ?? ''}
                        placeholder="0"
                        onChange={(e) => {
                          const v = e.target.value === '' ? null : Math.max(0, Number(e.target.value) || 0);
                          dispatch(updateWaypoint({ index: idx, waypoint: { ...wp, agreedPrice: v } }));
                        }}
                        style={{ width: 40, border: 'none', outline: 'none', background: 'transparent', fontSize: '0.6rem', color: '#92400e', fontWeight: 700, textAlign: 'right' }}
                      />
                    </Box>
                  </Tooltip>
                )}
                {/* OT context + price chips (set when the route was consolidated from OTs) */}
                {(wp.orderNumber || wp.agreedPrice != null || wp.waypointKind) && (
                  <>
                  {wp.waypointKind && (
                    <Chip size="small"
                      label={
                        wp.waypointKind === 'pickup' ? '↑ Recolha'
                        : wp.waypointKind === 'delivery' ? '↓ Entrega'
                        : wp.waypointKind === 'service' ? '🔧 Assistência'
                        : wp.waypointKind === 'fuel' ? '⛽ Comb.'
                        : wp.waypointKind === 'rest' ? '🛌 Pausa'
                        : wp.waypointKind}
                      style={{
                        height: 16, fontSize: '0.55rem', fontWeight: 700,
                        background: wp.waypointKind === 'pickup' ? '#dcfce7'
                          : wp.waypointKind === 'delivery' ? '#dbeafe'
                          : wp.waypointKind === 'service' ? '#ede9fe'
                          : wp.waypointKind === 'fuel' ? '#fef2f2'
                          : wp.waypointKind === 'rest' ? '#faf5ff'
                          : '#f1f5f9',
                        color: wp.waypointKind === 'pickup' ? '#16a34a'
                          : wp.waypointKind === 'delivery' ? '#2563eb'
                          : wp.waypointKind === 'service' ? '#7c3aed'
                          : wp.waypointKind === 'fuel' ? '#dc2626'
                          : wp.waypointKind === 'rest' ? '#7c3aed'
                          : '#64748b',
                      }} />
                  )}
                  {wp.orderNumber && (
                    <Chip size="small"
                      label={`OT ${wp.orderNumber}`}
                      style={{
                        height: 16, fontSize: '0.55rem', fontWeight: 700,
                        background: '#eef2ff', color: '#4338ca', border: '1px solid #c7d2fe',
                      }} />
                  )}
                  {wp.otStatus === 'delivered' && (
                    <Chip size="small"
                      label={lang === 'pt' ? '✓ Entregue' : '✓ Delivered'}
                      style={{
                        height: 16, fontSize: '0.55rem', fontWeight: 800,
                        background: '#dcfce7', color: '#15803d', border: '1px solid #86efac',
                      }} />
                  )}
                  {wp.agreedPrice != null && wp.agreedPrice > 0 && !(isMercadoria && wp.otId) && (
                    <Chip size="small"
                      label={`€ ${Number(wp.agreedPrice).toFixed(2)}`}
                      style={{
                        height: 16, fontSize: '0.55rem', fontWeight: 800,
                        background: 'linear-gradient(135deg, #fef3c7, #fde68a)',
                        color: '#92400e',
                      }} />
                  )}
                  </>
                )}
              </Box>
              {/* Document chips for this waypoint */}
              {documents.filter(d => d.waypointIndex === idx).length > 0 && (
                <Box style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 28, marginTop: 2, flexWrap: 'wrap' }}>
                  {documents.filter(d => d.waypointIndex === idx).map(doc => (
                    <Chip
                      key={doc.id}
                      size="small"
                      icon={<DescriptionIcon style={{ fontSize: 12 }} />}
                      label={doc.originalName.length > 18 ? doc.originalName.slice(0, 15) + '...' : doc.originalName}
                      onClick={() => window.open(doc.url, '_blank')}
                      onDelete={() => handleDocDelete(doc.id)}
                      style={{ height: 20, fontSize: '0.6rem', maxWidth: 160 }}
                      title={doc.originalName}
                    />
                  ))}
                </Box>
              )}
            </Box>
            {mapClickMode === `waypoint-${idx}` && (
              <Typography style={{ fontSize: '0.65rem', color: '#3b82f6', marginLeft: 36, marginTop: 2 }}>
                {t(lang, 'clickMapToSetLocation')}
              </Typography>
            )}
            <Box className={classes.connector} />
          </React.Fragment>
        ))}

        {/* Destination */}
        <Box style={{ position: 'relative' }}>
          <GeocoderInput
            label={t(lang, 'destination')}
            value={route.destination?.name || ''}
            lang={lang}
            icon={FlagIcon}
            iconColor="#ef4444"
            coordinates={route.destination?.coordinates}
            plateTooltip={route.destination?.vehiclePlate ? (lang === 'pt' ? `Veículo ${route.destination.vehiclePlate}` : `Vehicle ${route.destination.vehiclePlate}`) : undefined}
            onSelect={loc => dispatch(setDestination({ name: loc.name, coordinates: loc.coordinates }))}
            onMapPick={() => handleMapPick('destination')}
            onSave={handleSaveToLocations}
            savedLocations={savedLocations}
            showFavoritePicker
          />
          {/* Chips do destino — OT, tipo de paragem, preço acordado.
              Mesmo padrão visual dos waypoints intermédios, para o
              operador ver imediatamente qual OT está associada ao
              ponto final da rota. Antes desta linha, o destino tinha
              o local mas não dizia "OT-XXXX" — confundia o operador
              em rotas multi-stop. */}
          <Box style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 28, marginTop: 2, flexWrap: 'wrap' }}>
            {route.destination?.waypointKind && (
              <Chip
                size="small"
                label={
                  route.destination.waypointKind === 'pickup' ? '↑ Recolha'
                  : route.destination.waypointKind === 'delivery' ? '↓ Entrega'
                  : route.destination.waypointKind === 'service' ? '🔧 Assistência'
                  : route.destination.waypointKind === 'fuel' ? '⛽ Comb.'
                  : route.destination.waypointKind === 'rest' ? '🛌 Pausa'
                  : route.destination.waypointKind}
                style={{
                  height: 16, fontSize: '0.55rem', fontWeight: 700,
                  background: route.destination.waypointKind === 'pickup' ? '#dcfce7'
                    : route.destination.waypointKind === 'delivery' ? '#dbeafe'
                    : route.destination.waypointKind === 'service' ? '#ede9fe'
                    : route.destination.waypointKind === 'fuel' ? '#fef2f2'
                    : route.destination.waypointKind === 'rest' ? '#faf5ff'
                    : '#f1f5f9',
                  color: route.destination.waypointKind === 'pickup' ? '#16a34a'
                    : route.destination.waypointKind === 'delivery' ? '#2563eb'
                    : route.destination.waypointKind === 'service' ? '#7c3aed'
                    : route.destination.waypointKind === 'fuel' ? '#dc2626'
                    : route.destination.waypointKind === 'rest' ? '#7c3aed'
                    : '#64748b',
                }}
              />
            )}
            {(route.destination?.customRef || route.destination?.orderNumber) && (
              <Chip
                size="small"
                label={`OT ${route.destination.customRef || route.destination.orderNumber}`}
                style={{
                  height: 16, fontSize: '0.55rem', fontWeight: 700,
                  background: '#eef2ff', color: '#4338ca', border: '1px solid #c7d2fe',
                }}
              />
            )}
            {/* Custo da entrega editável no destino — mercadoria + ponto de OT. */}
            {isMercadoria && route.destination?.otId && (
              <Tooltip title={lang === 'pt' ? 'Custo da entrega (€) — sincroniza na OT ao recalcular' : 'Delivery cost (€) — synced to OT on recalc'}>
                <Box style={{ display: 'flex', alignItems: 'center', gap: 3, background: '#fff', border: '1px solid #fde68a', borderRadius: 4, padding: '1px 5px', height: 16 }}>
                  <Typography style={{ fontSize: '0.56rem', color: '#92400e', fontWeight: 800 }}>€</Typography>
                  <input
                    type="number" min="0" step="1"
                    value={route.destination.agreedPrice ?? ''}
                    placeholder="0"
                    onChange={(e) => {
                      const v = e.target.value === '' ? null : Math.max(0, Number(e.target.value) || 0);
                      dispatch(patchDestination({ agreedPrice: v }));
                    }}
                    style={{ width: 40, border: 'none', outline: 'none', background: 'transparent', fontSize: '0.6rem', color: '#92400e', fontWeight: 700, textAlign: 'right' }}
                  />
                </Box>
              </Tooltip>
            )}
            {route.destination?.agreedPrice != null && route.destination.agreedPrice > 0 && !(isMercadoria && route.destination?.otId) && (
              <Chip
                size="small"
                label={`€ ${Number(route.destination.agreedPrice).toFixed(2)}`}
                style={{
                  height: 16, fontSize: '0.55rem', fontWeight: 800,
                  background: 'linear-gradient(135deg, #fef3c7, #fde68a)',
                  color: '#92400e',
                }}
              />
            )}
            {documents.filter(d => d.waypointIndex === -2).map(doc => (
              <Chip
                key={doc.id}
                size="small"
                icon={<DescriptionIcon style={{ fontSize: 12 }} />}
                label={doc.originalName.length > 18 ? doc.originalName.slice(0, 15) + '...' : doc.originalName}
                onClick={() => window.open(doc.url, '_blank')}
                onDelete={() => handleDocDelete(doc.id)}
                style={{ height: 20, fontSize: '0.6rem', maxWidth: 160 }}
                title={doc.originalName}
              />
            ))}
          </Box>
        </Box>
        {mapClickMode === 'destination' && (
          <Typography style={{ fontSize: '0.65rem', color: '#3b82f6', marginLeft: 36, marginTop: 2 }}>
            {t(lang, 'clickMapDestination')}
          </Typography>
        )}
      </Box>

      {/* Add waypoint */}
      <Box style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <Button
          className={classes.addWpBtn}
          startIcon={<AddIcon style={{ fontSize: 14 }} />}
          onClick={handleAddWaypoint}
          size="small"
          style={{ flex: 1 }}
        >
          {t(lang, 'addWaypoint')}
        </Button>
        {/* + Ordem — adiciona uma OT pendente à rota actual. Disponível
            quando a rota já foi gravada (tem dbId) e está em draft, planned
            (despachada, motorista ainda não iniciou) OU in_progress (em
            trânsito — o backend add-ot aceita-o e o recálculo já exclui as
            OTs entregues + parte do GPS atual). Consistente com o "+OT" da
            lista de Rotas. Reaproveita o diálogo de Operações → Rotas. */}
        {route.dbId && ['draft', 'planned', 'in_progress'].includes(route.dbStatus) && (
          <Tooltip title={lang === 'pt' ? 'Adicionar ordem à rota' : 'Add order to route'}>
            <Button
              size="small"
              onClick={() => setAddOTDialogOpen(true)}
              style={{
                minWidth: 0, height: 28, padding: '0 10px', borderRadius: 8,
                background: 'rgba(37,99,235,0.10)',
                border: '1px solid rgba(37,99,235,0.25)',
                color: '#2563eb',
                fontSize: '0.66rem', fontWeight: 700, textTransform: 'none',
                gap: 4,
              }}
            >
              <AddIcon style={{ fontSize: 13 }} />
              {lang === 'pt' ? 'Ordem' : 'Order'}
            </Button>
          </Tooltip>
        )}
        {/* External "favorite" star button removed — each newly added waypoint
            now has its own ⭐ next to the geocoder input (same as origin/
            destination), so the favorite-as-waypoint flow is: click "Adicionar
            paragem intermédia" → empty waypoint appears → click ⭐ on it. */}
        <Box style={{ position: 'relative', display: 'none' }}>
          <Button
            size="small"
            onClick={() => {
              if (!showFavWpPicker && token && savedLocations.length === 0) {
                dispatch(fetchLocations(token));
              }
              setShowFavWpPicker(prev => !prev);
            }}
            style={{
              minWidth: 32, height: 28, padding: '0 8px', borderRadius: 8,
              background: showFavWpPicker ? 'rgba(245,158,11,0.15)' : 'rgba(0,0,0,0.04)',
              border: showFavWpPicker ? '1px solid rgba(245,158,11,0.4)' : '1px solid rgba(0,0,0,0.08)',
              color: showFavWpPicker ? '#f59e0b' : '#64748b',
            }}
            title={lang === 'pt' ? 'Adicionar favorito como ponto intermédio' : 'Add favorite as waypoint'}
          >
            <StarIcon style={{ fontSize: 16 }} />
          </Button>
          {showFavWpPicker && ReactDOM.createPortal(
            <Box style={{
              position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 9999,
              background: '#fff', borderRadius: 14, padding: '0',
              boxShadow: '0 12px 40px rgba(0,0,0,0.25), 0 0 0 1px rgba(0,0,0,0.06)',
              maxHeight: 440, width: 380, display: 'flex', flexDirection: 'column',
              overflow: 'hidden',
            }}>
              {/* Header */}
              <Box style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #e2e8f0', background: 'linear-gradient(180deg, #fff 0%, #fefce8 100%)' }}>
                <Typography style={{ fontSize: '0.82rem', fontWeight: 700, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <StarIcon style={{ fontSize: 16, color: '#eab308' }} />
                  {lang === 'pt' ? 'Favoritos' : 'Favorites'}
                  <span style={{ color: '#94a3b8', fontWeight: 400, fontSize: '0.68rem' }}>({savedLocations.length})</span>
                </Typography>
                <Box style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {routeCoords && (
                    <Box style={{ display: 'flex', gap: 3 }}>
                      {[10, 20, 50].map(d => (
                        <Box key={d} onClick={() => setMaxDist(d)} style={{
                          padding: '3px 8px', borderRadius: 6, cursor: 'pointer',
                          fontSize: '0.62rem', fontWeight: 600,
                          background: maxDist === d ? '#3b82f6' : '#f1f5f9',
                          color: maxDist === d ? '#fff' : '#94a3b8',
                          transition: 'all 0.15s',
                        }}>{d}km</Box>
                      ))}
                    </Box>
                  )}
                  <Button size="small" onClick={() => setShowFavWpPicker(false)}
                    style={{ minWidth: 0, padding: '2px 8px', fontSize: '0.68rem', color: '#64748b', textTransform: 'none', fontWeight: 600, borderRadius: 6, border: '1px solid #e2e8f0' }}>
                    Fechar
                  </Button>
                </Box>
              </Box>
              {/* List */}
              <Box style={{ flex: 1, overflowY: 'auto', padding: '6px 0' }}>
                {(routeCoords ? nearbyLocations : savedLocations).map(loc => {
                  const isInRoute = (route.waypoints || []).some(wp =>
                    wp.coordinates && Math.abs(wp.coordinates[0] - Number(loc.longitude)) < 0.005 && Math.abs(wp.coordinates[1] - Number(loc.latitude)) < 0.005
                  );
                  return (
                    <Box
                      key={loc.id}
                      onClick={() => {
                        if (!isInRoute) {
                          dispatch(addWaypoint({ name: loc.name, coordinates: [Number(loc.longitude), Number(loc.latitude)] }));
                          dispatch(setNeedsRecalculation(true));
                        }
                      }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10, padding: '8px 16px',
                        cursor: isInRoute ? 'default' : 'pointer', transition: 'background 0.12s',
                        background: isInRoute ? 'rgba(34,197,94,0.04)' : 'transparent',
                      }}
                      onMouseEnter={e => { if (!isInRoute) e.currentTarget.style.background = '#fafbfc'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = isInRoute ? 'rgba(34,197,94,0.04)' : 'transparent'; }}
                    >
                      <StarIcon style={{ fontSize: 16, color: isInRoute ? '#22c55e' : '#eab308' }} />
                      <Box style={{ flex: 1, minWidth: 0 }}>
                        <Typography style={{ fontSize: '0.78rem', fontWeight: 600, color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {loc.name}
                        </Typography>
                        <Typography style={{ fontSize: '0.62rem', color: '#94a3b8' }}>
                          {loc.distToRoute != null ? `${loc.distToRoute} km ${lang === 'pt' ? 'da rota' : 'from route'}` : loc.address || ''}
                        </Typography>
                      </Box>
                      {isInRoute ? (
                        <Typography style={{ fontSize: '0.62rem', color: '#22c55e', fontWeight: 700, flexShrink: 0 }}>
                          {lang === 'pt' ? 'Na rota' : 'In route'}
                        </Typography>
                      ) : (
                        <AddIcon style={{ fontSize: 16, color: '#3b82f6', flexShrink: 0 }} />
                      )}
                    </Box>
                  );
                })}
                {savedLocations.length === 0 && (
                  <Typography style={{ fontSize: '0.78rem', color: '#94a3b8', padding: '24px 16px', textAlign: 'center' }}>
                    {lang === 'pt' ? 'Sem favoritos guardados' : 'No saved favorites'}
                  </Typography>
                )}
                {routeCoords && nearbyLocations.length === 0 && savedLocations.length > 0 && (
                  <Typography style={{ fontSize: '0.78rem', color: '#94a3b8', padding: '24px 16px', textAlign: 'center' }}>
                    {lang === 'pt' ? `Nenhum favorito a menos de ${maxDist}km da rota` : `No favorites within ${maxDist}km of route`}
                  </Typography>
                )}
              </Box>
            </Box>,
            document.body
          )}
        </Box>
        {/* Excel + OT import buttons hidden at user request — the picker implementation below
            stays mounted so the keep-alive fetch logic remains intact. */}
        <input
          ref={excelInputRef}
          type="file"
          accept=".xlsx,.xls,.csv"
          style={{ display: 'none' }}
          onChange={handleExcelFileSelect}
        />
        <Box style={{ position: 'relative', display: 'none' }}>
          <Button
            size="small"
            onClick={handleFetchOTs}
            style={{
              minWidth: 32, height: 28, padding: '0 8px', borderRadius: 8,
              background: showOTPicker ? 'rgba(59,130,246,0.15)' : 'rgba(0,0,0,0.04)',
              border: showOTPicker ? '1px solid rgba(59,130,246,0.4)' : '1px solid rgba(0,0,0,0.08)',
              color: showOTPicker ? '#3b82f6' : '#64748b',
            }}
            title={lang === 'pt' ? 'Importar de Ordem de Transporte' : 'Import from Transport Order'}
          >
            <AssignmentIcon style={{ fontSize: 16 }} />
          </Button>
          {showOTPicker && ReactDOM.createPortal(
            <Box style={{
              position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 9999,
              background: '#fff', borderRadius: 14, padding: '0',
              boxShadow: '0 12px 40px rgba(0,0,0,0.25), 0 0 0 1px rgba(0,0,0,0.06)',
              maxHeight: 480, width: 420, display: 'flex', flexDirection: 'column',
              overflow: 'hidden',
            }}>
              {/* Header */}
              <Box style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #e2e8f0', background: 'linear-gradient(180deg, #fff 0%, #eff6ff 100%)' }}>
                <Typography style={{ fontSize: '0.82rem', fontWeight: 700, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <AssignmentIcon style={{ fontSize: 16, color: '#3b82f6' }} />
                  {lang === 'pt' ? 'Ordens de Transporte Pendentes' : 'Pending Transport Orders'}
                  <span style={{ color: '#94a3b8', fontWeight: 400, fontSize: '0.68rem' }}>({pendingOTs.length})</span>
                </Typography>
                <Button size="small" onClick={() => setShowOTPicker(false)}
                  style={{ minWidth: 0, padding: '2px 8px', fontSize: '0.68rem', color: '#64748b', textTransform: 'none', fontWeight: 600, borderRadius: 6, border: '1px solid #e2e8f0' }}>
                  Fechar
                </Button>
              </Box>
              {/* List */}
              <Box style={{ flex: 1, overflowY: 'auto', padding: '6px 0' }}>
                {otLoading ? (
                  <Box style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : pendingOTs.length === 0 ? (
                  <Typography style={{ fontSize: '0.78rem', color: '#94a3b8', padding: '24px 16px', textAlign: 'center' }}>
                    {lang === 'pt' ? 'Sem ordens de transporte pendentes' : 'No pending transport orders'}
                  </Typography>
                ) : pendingOTs.map(ot => (
                  <Box
                    key={ot.id || ot.orderNumber}
                    onClick={() => handleImportOT(ot)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px',
                      cursor: 'pointer', transition: 'background 0.12s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#f0f9ff'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    <AssignmentIcon style={{ fontSize: 16, color: '#3b82f6', flexShrink: 0 }} />
                    <Box style={{ flex: 1, minWidth: 0 }}>
                      <Box style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Typography style={{ fontSize: '0.78rem', fontWeight: 700, color: '#1e293b' }}>
                          {ot.orderNumber || 'OT'}
                        </Typography>
                        {(ot.cargoWeight || ot.cargoPallets) && (
                          <Typography style={{ fontSize: '0.6rem', color: '#94a3b8', fontWeight: 500 }}>
                            {[ot.cargoWeight ? `${ot.cargoWeight}kg` : null, ot.cargoPallets ? `${ot.cargoPallets} pal.` : null].filter(Boolean).join(' · ')}
                          </Typography>
                        )}
                      </Box>
                      <Typography style={{ fontSize: '0.7rem', color: '#475569', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {ot.pickupName || ot.pickupAddress || '?'} → {ot.deliveryName || ot.deliveryAddress || '?'}
                      </Typography>
                      {ot.cargoDescription && (
                        <Typography style={{ fontSize: '0.6rem', color: '#94a3b8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {ot.cargoDescription}
                        </Typography>
                      )}
                    </Box>
                    <AddIcon style={{ fontSize: 16, color: '#3b82f6', flexShrink: 0 }} />
                  </Box>
                ))}
              </Box>
            </Box>,
            document.body
          )}
        </Box>
      </Box>

      {/* Excel import dialog */}
      {showImportDialog && ReactDOM.createPortal(
        <Box style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 10000,
          background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }} onClick={() => { if (!importProgress || importProgress.status === 'done') { setShowImportDialog(false); } }}>
          <Paper
            elevation={12}
            style={{
              width: 440, maxHeight: '80vh', borderRadius: 14, overflow: 'hidden',
              display: 'flex', flexDirection: 'column',
            }}
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <Box style={{
              padding: '14px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              borderBottom: '1px solid #e2e8f0',
              background: 'linear-gradient(180deg, #fff 0%, #f0f9ff 100%)',
            }}>
              <Typography style={{ fontSize: '0.88rem', fontWeight: 700, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 8 }}>
                <CloudUploadIcon style={{ fontSize: 18, color: '#3b82f6' }} />
                {lang === 'pt' ? 'Importar Pontos de Excel' : 'Import Waypoints from Excel'}
              </Typography>
              <IconButton size="small" onClick={() => { if (!importProgress || importProgress.status === 'done') setShowImportDialog(false); }}
                style={{ padding: 4 }}>
                <CloseIcon style={{ fontSize: 16 }} />
              </IconButton>
            </Box>

            {/* Body */}
            <Box style={{ flex: 1, overflowY: 'auto', padding: '14px 18px' }}>
              {/* Preview table */}
              {!importProgress && importData.length > 0 && (
                <>
                  <Typography style={{ fontSize: '0.72rem', color: '#64748b', marginBottom: 8 }}>
                    {lang === 'pt'
                      ? `${importData.length} ponto${importData.length !== 1 ? 's' : ''} encontrado${importData.length !== 1 ? 's' : ''} no ficheiro:`
                      : `${importData.length} waypoint${importData.length !== 1 ? 's' : ''} found in file:`}
                  </Typography>
                  <Box style={{
                    maxHeight: 240, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 8,
                    fontSize: '0.72rem',
                  }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: '#f8fafc', position: 'sticky', top: 0 }}>
                          <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600, color: '#475569', borderBottom: '1px solid #e2e8f0', fontSize: '0.68rem' }}>#</th>
                          <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600, color: '#475569', borderBottom: '1px solid #e2e8f0', fontSize: '0.68rem' }}>
                            {lang === 'pt' ? 'Morada' : 'Address'}
                          </th>
                          <th style={{ padding: '6px 10px', textAlign: 'center', fontWeight: 600, color: '#475569', borderBottom: '1px solid #e2e8f0', fontSize: '0.68rem' }}>
                            {lang === 'pt' ? 'Tempo' : 'Duration'}
                          </th>
                          <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600, color: '#475569', borderBottom: '1px solid #e2e8f0', fontSize: '0.68rem' }}>
                            {lang === 'pt' ? 'Notas' : 'Notes'}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {importData.map((row, i) => (
                          <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                            <td style={{ padding: '5px 10px', color: '#94a3b8' }}>{i + 1}</td>
                            <td style={{ padding: '5px 10px', color: '#1e293b', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {row.address}
                            </td>
                            <td style={{ padding: '5px 10px', textAlign: 'center', color: row.stopDuration ? '#3b82f6' : '#cbd5e1' }}>
                              {row.stopDuration ? `${row.stopDuration} min` : '-'}
                            </td>
                            <td style={{ padding: '5px 10px', color: '#64748b', maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {row.notes || '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Box>
                </>
              )}

              {/* Progress */}
              {importProgress && importProgress.status === 'geocoding' && (
                <Box style={{ textAlign: 'center', padding: '20px 0' }}>
                  <CircularProgress size={36} style={{ color: '#3b82f6' }} />
                  <Typography style={{ fontSize: '0.78rem', color: '#1e293b', marginTop: 10, fontWeight: 600 }}>
                    {lang === 'pt' ? 'A geocodificar moradas...' : 'Geocoding addresses...'}
                  </Typography>
                  <Typography style={{ fontSize: '0.72rem', color: '#64748b', marginTop: 4 }}>
                    {importProgress.current} / {importProgress.total}
                  </Typography>
                  <Box style={{
                    height: 6, borderRadius: 3, background: '#e2e8f0', marginTop: 12, overflow: 'hidden',
                  }}>
                    <Box style={{
                      height: '100%', borderRadius: 3,
                      background: 'linear-gradient(90deg, #3b82f6, #6366f1)',
                      width: `${(importProgress.current / importProgress.total) * 100}%`,
                      transition: 'width 0.3s ease',
                    }} />
                  </Box>
                </Box>
              )}

              {/* Results */}
              {importResults && (
                <Box style={{ padding: '16px 0' }}>
                  <Box style={{
                    display: 'flex', gap: 12, justifyContent: 'center', marginBottom: 12,
                  }}>
                    <Box style={{
                      padding: '10px 18px', borderRadius: 10,
                      background: 'rgba(34,197,94,0.1)', textAlign: 'center',
                    }}>
                      <Typography style={{ fontSize: '1.1rem', fontWeight: 700, color: '#22c55e' }}>
                        {importResults.imported}
                      </Typography>
                      <Typography style={{ fontSize: '0.62rem', color: '#16a34a', fontWeight: 600 }}>
                        {lang === 'pt' ? 'Importados' : 'Imported'}
                      </Typography>
                    </Box>
                    {importResults.failed > 0 && (
                      <Box style={{
                        padding: '10px 18px', borderRadius: 10,
                        background: 'rgba(239,68,68,0.1)', textAlign: 'center',
                      }}>
                        <Typography style={{ fontSize: '1.1rem', fontWeight: 700, color: '#ef4444' }}>
                          {importResults.failed}
                        </Typography>
                        <Typography style={{ fontSize: '0.62rem', color: '#dc2626', fontWeight: 600 }}>
                          {lang === 'pt' ? 'Falhados' : 'Failed'}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {/* Error details */}
                  {importErrors.length > 0 && (
                    <Box style={{
                      maxHeight: 120, overflowY: 'auto', border: '1px solid rgba(239,68,68,0.2)',
                      borderRadius: 8, background: 'rgba(239,68,68,0.03)', padding: '8px 10px',
                    }}>
                      {importErrors.map((err, i) => (
                        <Box key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 0' }}>
                          <ErrorOutlineIcon style={{ fontSize: 13, color: '#ef4444', flexShrink: 0 }} />
                          <Typography style={{ fontSize: '0.66rem', color: '#7f1d1d' }}>
                            <strong>{err.address}</strong> — {err.reason}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              )}
            </Box>

            {/* Footer */}
            <Box style={{
              padding: '12px 18px', borderTop: '1px solid #e2e8f0',
              display: 'flex', justifyContent: 'flex-end', gap: 8,
            }}>
              {!importProgress && (
                <>
                  <Button
                    size="small"
                    onClick={() => setShowImportDialog(false)}
                    style={{ fontSize: '0.72rem', textTransform: 'none', color: '#64748b' }}
                  >
                    {lang === 'pt' ? 'Cancelar' : 'Cancel'}
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleImportWaypoints}
                    disabled={importData.length === 0}
                    style={{
                      fontSize: '0.72rem', textTransform: 'none', fontWeight: 600,
                      background: 'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)',
                      color: '#fff', borderRadius: 8, padding: '6px 18px',
                      boxShadow: '0 2px 8px rgba(59,130,246,0.3)',
                    }}
                  >
                    {lang === 'pt' ? `Importar ${importData.length} ponto${importData.length !== 1 ? 's' : ''}` : `Import ${importData.length} waypoint${importData.length !== 1 ? 's' : ''}`}
                  </Button>
                </>
              )}
              {importProgress && importProgress.status === 'done' && (
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => {
                    setShowImportDialog(false);
                    setImportData([]);
                    setImportProgress(null);
                    setImportErrors([]);
                    setImportResults(null);
                  }}
                  style={{
                    fontSize: '0.72rem', textTransform: 'none', fontWeight: 600,
                    background: '#22c55e', color: '#fff', borderRadius: 8, padding: '6px 18px',
                  }}
                >
                  {lang === 'pt' ? 'Fechar' : 'Close'}
                </Button>
              )}
            </Box>
          </Paper>
        </Box>,
        document.body
      )}

      </CollapsibleSection>

      {/* ═══ Section 2: Veiculo & Horario ═══ */}
      <CollapsibleSection
        title={lang === 'pt' ? 'Veículo & Horário' : 'Vehicle & Schedule'}
        icon={<LocalShippingIcon style={{ fontSize: 14, color: '#3b82f6' }} />}
        iconColor="#3b82f6"
        iconBg="#eff6ff"
        open={openSections.vehicle}
        onToggle={() => toggleSection('vehicle')}
        badge={(() => {
          // Fall back to the client's preferred sub-type before the hard-coded "cisterna"
          const subType = opts.vehicleSubType
            || clientSettings?.defaultVehicleSubType
            || (opts.vehicleType === 'car' ? 'car' : 'cisterna');
          const labels = { cisterna: 'Cisterna', frigorifico: lang === 'pt' ? 'Frigorífico' : 'Reefer', light_truck: lang === 'pt' ? 'Ligeiro' : 'Light', general_cargo: lang === 'pt' ? 'Mercadorias' : 'Cargo', car: lang === 'pt' ? 'Carro' : 'Car' };
          return (
            <Box component="span" style={{ background: '#dbeafe', color: '#2563eb', borderRadius: 8, padding: '1px 6px', fontSize: '0.58rem', fontWeight: 700 }}>
              {labels[subType] || labels.cisterna}
            </Box>
          );
        })()}
      >
        {/* Quick vehicle type selector — truck icons */}
        <Box style={{ display: 'flex', gap: 3, marginBottom: 6 }}>
          {[
            { v: 'cisterna', tip: lang === 'pt' ? 'Camião Cisterna (40t)' : 'Tanker (40t)', svg: <svg viewBox="0 0 24 24" width="18" height="18"><ellipse cx="12" cy="11" rx="8" ry="5" fill="none" stroke="currentColor" strokeWidth="1.8"/><rect x="2" y="14" width="20" height="3" rx="1" fill="currentColor" opacity="0.3"/><circle cx="6" cy="18" r="1.5" fill="currentColor"/><circle cx="18" cy="18" r="1.5" fill="currentColor"/></svg> },
            { v: 'frigorifico', tip: lang === 'pt' ? 'Camião Frigorífico (26t)' : 'Reefer (26t)', svg: <svg viewBox="0 0 24 24" width="18" height="18"><rect x="3" y="6" width="14" height="11" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.8"/><rect x="17" y="9" width="4" height="8" rx="1" fill="none" stroke="currentColor" strokeWidth="1.5"/><line x1="10" y1="8" x2="10" y2="15" stroke="currentColor" strokeWidth="1" opacity="0.5"/><line x1="6" y1="11.5" x2="14" y2="11.5" stroke="currentColor" strokeWidth="1" opacity="0.5"/><circle cx="7" cy="19" r="1.5" fill="currentColor"/><circle cx="19" cy="19" r="1.5" fill="currentColor"/></svg> },
            { v: 'light_truck', tip: lang === 'pt' ? 'Camião Ligeiro (7.5t)' : 'Light Truck (7.5t)', svg: <svg viewBox="0 0 24 24" width="18" height="18"><rect x="1" y="7" width="12" height="10" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.8"/><path d="M13 10 h5 l3 4 v3 h-8 z" fill="none" stroke="currentColor" strokeWidth="1.5"/><circle cx="6" cy="19" r="1.5" fill="currentColor"/><circle cx="18" cy="19" r="1.5" fill="currentColor"/></svg> },
            { v: 'general_cargo', tip: lang === 'pt' ? 'Mercadorias (24t)' : 'General Cargo (24t)', svg: <svg viewBox="0 0 24 24" width="18" height="18"><rect x="1" y="6" width="15" height="11" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.8"/><path d="M16 9 h4 l2 4 v4 h-6 z" fill="none" stroke="currentColor" strokeWidth="1.5"/><rect x="4" y="8" width="4" height="4" rx="0.5" fill="currentColor" opacity="0.3"/><rect x="9" y="8" width="4" height="4" rx="0.5" fill="currentColor" opacity="0.3"/><circle cx="6" cy="19" r="1.5" fill="currentColor"/><circle cx="19" cy="19" r="1.5" fill="currentColor"/></svg> },
            { v: 'car', tip: lang === 'pt' ? 'Carro Ligeiro' : 'Car', svg: <svg viewBox="0 0 24 24" width="18" height="18"><path d="M5 14 L7 8 h10 l2 6" fill="none" stroke="currentColor" strokeWidth="1.8"/><rect x="3" y="14" width="18" height="4" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.5"/><circle cx="7" cy="19" r="1.5" fill="currentColor"/><circle cx="17" cy="19" r="1.5" fill="currentColor"/></svg> },
          ].map(({ v, tip, svg }) => {
            const currentSub = opts.vehicleSubType || (opts.vehicleType === 'car' ? 'car' : 'cisterna');
            const active = currentSub === v;
            const defaults = {
              cisterna: { vehicleType: 'hgv', truckWeight: '40', truckHeight: '4.0', truckWidth: '2.55', truckLength: '18.75', truckAxleLoad: '11.5', truckAxleCount: '5', emissionType: 'EURO_6' },
              frigorifico: { vehicleType: 'hgv', truckWeight: '26', truckHeight: '4.0', truckWidth: '2.55', truckLength: '13.6', truckAxleLoad: '10', truckAxleCount: '4', emissionType: 'EURO_6' },
              light_truck: { vehicleType: 'hgv', truckWeight: '7.5', truckHeight: '3.0', truckWidth: '2.2', truckLength: '7.5', truckAxleLoad: '5', truckAxleCount: '2', emissionType: 'EURO_6' },
              general_cargo: { vehicleType: 'hgv', truckWeight: '24', truckHeight: '4.0', truckWidth: '2.55', truckLength: '12.0', truckAxleLoad: '10', truckAxleCount: '3', emissionType: 'EURO_6' },
              car: { vehicleType: 'car' },
            };
            return (
              <Tooltip key={v} title={tip}>
                <Box
                  onClick={() => {
                    dispatch(setRouteOptions({
                      vehicleSubType: v,
                      ...defaults[v],
                      // Mesma regra do picker grande: tacho só liga em
                      // pesados e SE o cliente o tiver explicitamente ON.
                      useTachograph: defaults[v].vehicleType !== 'car' && clientSettings?.useTachograph === true,
                      // Mudar de veículo é uma nova decisão — limpa
                      // override anterior para que o useEffect volte a
                      // ler a preferência do cliente em mudanças futuras.
                      tachoOverride: false,
                    }));
                    // Auto-recalcular a rota — uma mudança de veículo
                    // altera dimensões (peso, eixos, comprimento) que
                    // afectam tanto o trajeto como custos. Antes o
                    // operador tinha de clicar manualmente "Recalcular".
                    // O useEffect na linha 1330 trata da chamada se a
                    // rota actual tiver origem+destino válidos.
                    if (route.computedRoute) {
                      dispatch(setNeedsRecalculation(true));
                    }
                  }}
                  style={{
                    width: 30, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    borderRadius: 6, cursor: 'pointer',
                    color: active ? '#3b82f6' : '#94a3b8',
                    background: active ? 'rgba(59,130,246,0.10)' : 'transparent',
                    border: `1.5px solid ${active ? '#3b82f6' : '#e2e8f0'}`,
                    transition: 'all 0.15s',
                  }}
                >
                  {svg}
                </Box>
              </Tooltip>
            );
          })}

        </Box>

        {/* Tachograph toggle — its own row so it's clearly visible. The
            operator can override the client default per route (e.g. enable
            tacho on a Petrotec heavy-vehicle delivery even though the
            client default is off). Disabled only for light vehicles. */}
        {(() => {
          const isCar = route.routeOptions?.vehicleType === 'car';
          const clientOn = clientSettings?.useTachograph === true;
          const disabled = isCar;
          const explicit = route.routeOptions?.useTachograph;
          // ON SE: (a) operador forçou ON nesta rota OU
          //        (b) operador não forçou OFF E a preferência do cliente é ON.
          // Alinhado com o gate estrito — `clientOn === true`.
          const on = !isCar && (explicit === true || (explicit !== false && clientOn));
          const tooltip = isPt => isCar
            ? (isPt ? 'Tacógrafo não se aplica a veículos ligeiros' : 'Tachograph N/A on light vehicles')
            : on
              ? (isPt
                  ? (clientOn ? 'Tacógrafo ACTIVO (default do cliente)' : 'Tacógrafo ACTIVO (override desta rota)')
                  : (clientOn ? 'Tachograph ON (client default)' : 'Tachograph ON (per-route override)'))
              : (isPt ? 'Tacógrafo desativado · clica para activar' : 'Tachograph off · click to enable');
          return (
            <Tooltip title={tooltip(lang === 'pt')}>
              <Box
                onClick={disabled ? undefined : () => dispatch(setRouteOptions({ useTachograph: !on, tachoOverride: true }))}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  userSelect: 'none',
                  marginTop: 8, padding: '8px 10px', borderRadius: 8,
                  background: on ? 'linear-gradient(135deg, #faf5ff, #ede9fe)' : '#f8fafc',
                  border: `1.5px solid ${on ? '#8b5cf6' : '#e2e8f0'}`,
                  opacity: disabled ? 0.5 : 1,
                  transition: 'all 0.15s',
                }}
              >
                <Box style={{
                  width: 28, height: 16, borderRadius: 8,
                  background: on ? '#8b5cf6' : '#cbd5e1',
                  display: 'flex', alignItems: 'center',
                  padding: 1, transition: 'background 0.15s',
                }}>
                  <Box style={{
                    width: 12, height: 12, borderRadius: '50%',
                    background: '#fff',
                    transform: on ? 'translateX(12px)' : 'translateX(0)',
                    transition: 'transform 0.15s',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
                  }} />
                </Box>
                <ScheduleIcon style={{ fontSize: 14, color: on ? '#7c3aed' : '#64748b' }} />
                <Box style={{ flex: 1 }}>
                  <Typography style={{ fontSize: '0.72rem', fontWeight: 700, color: on ? '#5b21b6' : '#475569' }}>
                    {lang === 'pt' ? 'Tacógrafo' : 'Tachograph'}
                  </Typography>
                  <Typography style={{ fontSize: '0.58rem', color: on ? '#7c3aed' : '#94a3b8' }}>
                    {on
                      ? (lang === 'pt' ? 'Paragens obrigatórias UE 561 calculadas' : 'EU 561 mandatory stops computed')
                      : (lang === 'pt' ? 'Sem cálculo de paragens' : 'No stops computed')}
                  </Typography>
                </Box>
              </Box>
            </Tooltip>
          );
        })()}

        {/* Route name & cargo */}
        <TextField
          className={classes.inputField}
          variant="outlined"
          size="small"
          label={t(lang, 'routeName')}
          value={route.name || ''}
          onChange={e => dispatch(setRouteName(e.target.value))}
          InputLabelProps={{ shrink: true, style: { fontSize: '0.75rem' } }}
          inputRef={nameInputRef}
          style={{ marginBottom: 8 }}
        />
        <TextField
          className={classes.inputField}
          variant="outlined"
          size="small"
          label={t(lang, 'cargoRef')}
          value={route.cargoRef || ''}
          onChange={e => dispatch(setCargoRef(e.target.value))}
          InputLabelProps={{ shrink: true, style: { fontSize: '0.75rem' } }}
          style={{ marginBottom: 8 }}
        />
        <DateTimePicker
          label={t(lang, 'departureTime')}
          value={route.departureTime || ''}
          onChange={val => dispatch(setDepartureTime(val))}
          compact
          tooltip="Data e hora prevista de partida. Usada para calcular a hora de chegada e validar a janela horária."
        />

        {/* Versao3: Arrival time window */}
        {/* Janela horária de chegada — parte do planeamento. O módulo
            'versao3' era o nome de uma RELEASE, não uma capacidade: removido. */}
        <>
          <Box style={{ marginTop: 8 }}>
            <Typography style={{ fontSize: '0.62rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
              <ScheduleIcon style={{ fontSize: 12, verticalAlign: 'middle', marginRight: 4 }} />
              Horário de Chegada
            </Typography>
            <Box style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              <DateTimePicker
                label="De:"
                compact
                value={route.arrivalTimeWindow?.start || ''}
                onChange={val => dispatch(setArrivalTimeWindow({ ...route.arrivalTimeWindow, start: val || null }))}
              />
              <DateTimePicker
                label="A:"
                compact
                value={route.arrivalTimeWindow?.end || ''}
                onChange={val => dispatch(setArrivalTimeWindow({ ...route.arrivalTimeWindow, end: val || null }))}
              />
            </Box>
          </Box>
        </>
      </CollapsibleSection>

      {/* ═══ Section 3: Opcoes ═══ */}
      <CollapsibleSection
        title={lang === 'pt' ? 'Opções' : 'Options'}
        icon={<TuneIcon style={{ fontSize: 14, color: '#8b5cf6' }} />}
        iconColor="#8b5cf6"
        iconBg="#faf5ff"
        open={openSections.options}
        onToggle={() => toggleSection('options')}
        badge={(() => {
          const condCount = [opts.avoidTolls, opts.avoidFerries, opts.avoidMotorways, opts.avoidTunnels, opts.avoidUnpaved, opts.avoidLEZ].filter(Boolean).length + (opts.avoidUrbanAreas || []).length;
          return condCount > 0 ? (
            <Box component="span" style={{ background: '#ef4444', color: '#fff', borderRadius: 8, padding: '1px 6px', fontSize: '0.58rem', fontWeight: 700 }}>
              {condCount}
            </Box>
          ) : null;
        })()}
      >
        <Box style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <Box
            onClick={() => setPopupOpen(o => !o)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              cursor: 'pointer', userSelect: 'none',
              padding: '4px 10px 4px 8px', borderRadius: 20,
              background: popupOpen ? 'rgba(59,130,246,0.1)' : 'rgba(0,0,0,0.04)',
              border: `1px solid ${popupOpen ? 'rgba(59,130,246,0.35)' : 'rgba(0,0,0,0.1)'}`,
              color: popupOpen ? '#3b82f6' : '#64748b',
              transition: 'all 0.2s',
            }}
            onMouseEnter={e => { if (!popupOpen) { e.currentTarget.style.background = 'rgba(0,0,0,0.07)'; e.currentTarget.style.color = '#475569'; } }}
            onMouseLeave={e => { if (!popupOpen) { e.currentTarget.style.background = 'rgba(0,0,0,0.04)'; e.currentTarget.style.color = '#64748b'; } }}
          >
            <TuneIcon style={{ fontSize: 13 }} />
            <Typography style={{ fontSize: '0.7rem', fontWeight: 600 }}>
              {lang === 'pt' ? 'Configurações de Rota' : 'Route Settings'}
            </Typography>
            {(() => {
              const condCount = [opts.avoidTolls, opts.avoidFerries, opts.avoidMotorways, opts.avoidTunnels, opts.avoidUnpaved, opts.avoidLEZ].filter(Boolean).length + (opts.avoidUrbanAreas || []).length;
              return condCount > 0 ? (
                <Box component="span" style={{ background: '#ef4444', color: '#fff', borderRadius: 8, padding: '0 5px', fontSize: '0.58rem', fontWeight: 700 }}>
                  {condCount}
                </Box>
              ) : null;
            })()}
          </Box>
          <FormControlLabel
            style={{ margin: 0 }}
            control={
              <Checkbox
                size="small"
                checked={compareMode}
                onChange={e => setCompareMode(e.target.checked)}
                color="primary"
                style={{ padding: 2 }}
              />
            }
            label={
              <Box style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                <CompareArrowsIcon style={{ fontSize: 13, color: compareMode ? '#3b82f6' : '#94a3b8' }} />
                <Typography style={{ fontSize: '0.68rem', color: compareMode ? '#1e293b' : '#64748b', fontWeight: compareMode ? 600 : 400 }}>
                  {lang === 'pt' ? 'Comparar rotas' : 'Compare routes'}
                </Typography>
              </Box>
            }
          />
          {/* Toggle leve — só pinta alternativas no mapa, sem abrir o
              diálogo de comparação. Esconde-se quando "Comparar" está
              activo porque esse já implica mostrar as alternativas. */}
          {!compareMode && (
            <FormControlLabel
              style={{ margin: 0 }}
              control={
                <Checkbox
                  size="small"
                  checked={showAlternatives}
                  onChange={e => setShowAlternatives(e.target.checked)}
                  color="primary"
                  style={{ padding: 2 }}
                />
              }
              label={
                <Tooltip title={lang === 'pt'
                  ? 'Calcula até 2 rotas alternativas e mostra-as no mapa em tracejado cinza'
                  : 'Computes up to 2 alternative routes shown as dashed gray lines on the map'}>
                  <Box style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                    <CallSplitIcon style={{ fontSize: 13, color: showAlternatives ? '#3b82f6' : '#94a3b8' }} />
                    <Typography style={{ fontSize: '0.68rem', color: showAlternatives ? '#1e293b' : '#64748b', fontWeight: showAlternatives ? 600 : 400 }}>
                      {lang === 'pt' ? 'Mostrar alternativas' : 'Show alternatives'}
                    </Typography>
                  </Box>
                </Tooltip>
              }
            />
          )}
        </Box>
      </CollapsibleSection>

      {/* ═══ Section 4: Custo padrão ═══ */}
      {(() => {
        const curSymbol = getCurrencySymbol(currency);
        return (
          <CollapsibleSection
            title={`${lang === 'pt' ? 'Custo padrão' : 'Standard cost'} (${curSymbol})`}
            icon={
              <Typography style={{ fontSize: 11, color: '#059669', fontWeight: 800 }}>
                {curSymbol}
              </Typography>
            }
            iconColor="#059669"
            iconBg="#ecfdf5"
            open={openSections.transportCost}
            onToggle={() => toggleSection('transportCost')}
            badge={route.estimatedCost != null && route.estimatedCost !== '' ? (
              <Box component="span" style={{ background: '#059669', color: '#fff', borderRadius: 8, padding: '1px 6px', fontSize: '0.58rem', fontWeight: 700 }}>
                {Number(route.estimatedCost).toFixed(2)} {curSymbol}
              </Box>
            ) : null}
          >
            <TextField
              value={route.estimatedCost != null && route.estimatedCost !== '' ? route.estimatedCost : ''}
              onChange={(e) => {
                const v = e.target.value;
                dispatch(setEstimatedCost(v === '' ? null : Number(v)));
              }}
              type="number"
              size="small"
              variant="outlined"
              placeholder="0.00"
              fullWidth
              label={`${lang === 'pt' ? 'Valor' : 'Value'} (${curSymbol})`}
              InputProps={{
                startAdornment: <Typography style={{ fontSize: '0.82rem', color: '#64748b', fontWeight: 600, marginRight: 6 }}>{curSymbol}</Typography>,
              }}
              inputProps={{ min: 0, step: 0.01 }}
            />
          </CollapsibleSection>
        );
      })()}

      {/* Action buttons */}
      {route.status === 'calculating' ? (
        <Button
          fullWidth
          variant="contained"
          className={classes.calcBtnCalculating}
          startIcon={<HourglassEmptyIcon style={{ animation: 'spin 1.2s linear infinite' }} />}
          disabled
        >
          {t(lang, 'calculating')}
        </Button>
      ) : route.status === 'ready' ? (
        <Button
          fullWidth
          variant="contained"
          className={classes.calcBtnReady}
          startIcon={<CheckCircleIcon />}
          onClick={handleCalculate}
        >
          {t(lang, 'routeReady')} · {t(lang, 'recalculate')}{
            opts.vehicleType === 'car' ? ` · ${t(lang, 'vehicleCar')}` :
            opts.vehicleSubType === 'cisterna' ? ' · Cisterna' :
            opts.vehicleSubType === 'frigorifico' ? ` · ${lang === 'pt' ? 'Frigorífico' : 'Reefer'}` :
            opts.vehicleSubType === 'light_truck' ? ` · ${lang === 'pt' ? 'Ligeiro' : 'Light'}` :
            opts.vehicleSubType === 'general_cargo' ? ` · ${lang === 'pt' ? 'Mercadorias' : 'Cargo'}` :
            ''
          }
        </Button>
      ) : (
        <Button
          fullWidth
          variant="contained"
          className={classes.calcBtn}
          startIcon={<DirectionsIcon />}
          onClick={handleCalculate}
          disabled={!canCalculate}
        >
          {t(lang, 'calculateRoute')}{
            opts.vehicleType === 'car' ? ` · ${t(lang, 'vehicleCar')}` :
            opts.vehicleSubType === 'cisterna' ? ' · Cisterna' :
            opts.vehicleSubType === 'frigorifico' ? ` · ${lang === 'pt' ? 'Frigorífico' : 'Reefer'}` :
            opts.vehicleSubType === 'light_truck' ? ` · ${lang === 'pt' ? 'Ligeiro' : 'Light'}` :
            opts.vehicleSubType === 'general_cargo' ? ` · ${lang === 'pt' ? 'Mercadorias' : 'Cargo'}` :
            ''
          }
        </Button>
      )}


      <Box style={{ display: 'flex', gap: 8 }}>
        {canUndo && (route.origin || route.destination) && (
          <Button
            variant="outlined"
            startIcon={<UndoIcon />}
            onClick={() => dispatch(undoRoute())}
            style={{
              fontSize: '0.75rem', fontWeight: 600, borderRadius: 10,
              borderColor: '#f59e0b', color: '#f59e0b', textTransform: 'none',
              minWidth: 0, padding: '6px 12px',
            }}
          >
            {t(lang, 'undo')}
          </Button>
        )}
        <Button
          fullWidth
          variant="outlined"
          className={classes.clearBtn}
          startIcon={<ClearIcon />}
          onClick={() => {
            dispatch(clearRoute());
            // Rota nova → limpa os preços definidos à mão (overrides por país),
            // para não "vazarem" para a próxima rota.
            dispatch(clearFuelPriceOverrides());
            dispatch(setMapClickMode(null));
            dispatch(setShowDetails(false));
            dispatch(setDisplayedSavedRoutes([]));
            dispatch(setResetMapView(true));
            setTimeout(() => nameInputRef.current?.focus(), 50);
          }}
        >
          {t(lang, 'clearRoute')}
        </Button>
      </Box>

      {/* Floating non-modal popup — rendered via Portal to escape backdropFilter/overflow */}
      {popupOpen && ReactDOM.createPortal(
        <Paper elevation={8} style={{
          position: 'fixed',
          left: popupPos.x,
          top: 62,
          width: 360,
          height: 'calc(100vh - 80px)',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 14,
          zIndex: 1000,
          overflow: 'hidden',
          boxShadow: '0 12px 40px rgba(0,0,0,0.22), 0 0 0 1px rgba(0,0,0,0.06)',
        }}>
          {/* Header — drag handle + title + close */}
          <Box
            onMouseDown={onPopupHeaderMouseDown}
            style={{
              display: 'flex', alignItems: 'center',
              borderBottom: '1px solid #f1f5f9',
              padding: '8px 10px',
              gap: 8, flexShrink: 0,
              cursor: 'grab', userSelect: 'none',
            }}
          >
            <TuneIcon style={{ fontSize: 15, color: '#3b82f6' }} />
            <Typography style={{ fontSize: '0.78rem', fontWeight: 700, color: '#1e293b', flex: 1 }}>
              {lang === 'pt' ? 'Configurações de Rota' : 'Route Settings'}
            </Typography>
            <IconButton size="small" onMouseDown={e => e.stopPropagation()} onClick={() => setPopupOpen(false)} style={{ width: 26, height: 26, color: '#94a3b8' }}>
              <CloseIcon style={{ fontSize: 15 }} />
            </IconButton>
          </Box>

          {/* Content */}
          <Box style={{ overflowY: 'auto', flex: 1, padding: '12px 14px' }}>

            {/* ── Configurações de Rota ─────────────────────────────── */}
            {(<>
              {/* ── Vehicle Type — icon toolbar ── */}
              <Typography style={{ fontSize: '0.6rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>{t(lang, 'vehicleTypeLbl')}</Typography>
              <Box style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 6, marginBottom: 10 }}>
                {[
                  // truckAxleCount + emissionType são críticos para a HERE
                  // calcular correctamente as portagens (sem eles assume
                  // 5+ eixos + EURO 3 = tarifa premium em FR/DE/AT).
                  { v: 'cisterna', icon: '🛢', label: lang === 'pt' ? 'Camião Cisterna' : 'Tanker Truck', type: 'hgv', defaults: { truckWeight: '40', truckHeight: '4.0', truckWidth: '2.55', truckLength: '18.75', truckAxleLoad: '11.5', truckAxleCount: '5', emissionType: 'EURO_6' } },
                  { v: 'frigorifico', icon: '❄', label: lang === 'pt' ? 'Camião Frigorífico' : 'Reefer Truck', type: 'hgv', defaults: { truckWeight: '26', truckHeight: '4.0', truckWidth: '2.55', truckLength: '13.6', truckAxleLoad: '10', truckAxleCount: '4', emissionType: 'EURO_6' } },
                  { v: 'light_truck', icon: '🚚', label: lang === 'pt' ? 'Camião Ligeiro' : 'Light Truck', type: 'hgv', defaults: { truckWeight: '7.5', truckHeight: '3.0', truckWidth: '2.2', truckLength: '7.5', truckAxleLoad: '5', truckAxleCount: '2', emissionType: 'EURO_6' } },
                  { v: 'general_cargo', icon: '📦', label: lang === 'pt' ? 'Mercadorias' : 'General Cargo', type: 'hgv', defaults: { truckWeight: '24', truckHeight: '4.0', truckWidth: '2.55', truckLength: '12.0', truckAxleLoad: '10', truckAxleCount: '3', emissionType: 'EURO_6' } },
                  { v: 'car', icon: '🚗', label: lang === 'pt' ? 'Carro' : 'Car', type: 'car', defaults: {} },
                ].map(({ v, icon, label, type, defaults }) => {
                  const currentSub = opts.vehicleSubType || (opts.vehicleType === 'car' ? 'car' : 'cisterna');
                  const active = currentSub === v;
                  return (
                    <Box key={v}
                      onClick={() => {
                        dispatch(setRouteOptions({
                          vehicleType: type,
                          vehicleSubType: v,
                          ...defaults,
                          // Opt-in estrito (igual ao gate do useEffect e do
                          // SidebarPanel): só ligamos tacho ao escolher
                          // veículo pesado SE o cliente o tiver activado
                          // explicitamente. Cobre o caso de o operador
                          // clicar no preset antes de o /auth/me devolver
                          // a preferência (clientSettings ainda `{}`).
                          useTachograph: type !== 'car' && clientSettings?.useTachograph === true,
                          // Limpa override anterior — mudar de veículo é
                          // uma decisão nova, o operador volta ao default
                          // do cliente até clicar no toggle outra vez.
                          tachoOverride: false,
                        }));
                        // Auto-recalcular se já existe rota — mudar de
                        // veículo altera dimensões/peso/eixos que afectam
                        // trajeto, combustível e portagens.
                        if (route.computedRoute) {
                          dispatch(setNeedsRecalculation(true));
                        }
                      }}
                      style={{
                        textAlign: 'center', padding: '10px 4px 8px', borderRadius: 10, cursor: 'pointer',
                        background: active ? 'linear-gradient(135deg, #3b82f6, #1d4ed8)' : '#f8fafc',
                        color: active ? '#ffffff' : '#475569',
                        border: `1.5px solid ${active ? '#3b82f6' : '#e2e8f0'}`,
                        transition: 'all 0.2s ease',
                        boxShadow: active ? '0 2px 8px rgba(59,130,246,0.3)' : 'none',
                      }}
                    >
                      <div style={{ fontSize: '1.3rem', marginBottom: 3, filter: active ? 'brightness(1.2)' : 'none' }}>{icon}</div>
                      <div style={{ fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.01em', lineHeight: 1.2 }}>{label}</div>
                    </Box>
                  );
                })}
              </Box>

              {/* ── Fuel ── */}
              <Typography style={{ fontSize: '0.6rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>{t(lang, 'fuel')}</Typography>
              <Box style={{ display: 'flex', gap: 5, marginBottom: 6 }}>
                {[{ v: 'diesel', l: t(lang, 'fuelDiesel') }, { v: 'gasoline', l: t(lang, 'fuelGasoline') }].map(({ v, l }) => (
                  <Box key={v} onClick={() => dispatch(setFuelType(v))}
                    style={{ flex: 1, textAlign: 'center', padding: '5px 0', borderRadius: 6, cursor: 'pointer', fontSize: '0.7rem', fontWeight: 600,
                      background: fuelType === v ? '#3b82f6' : 'rgba(0,0,0,0.04)', color: fuelType === v ? '#fff' : '#64748b',
                      border: `1px solid ${fuelType === v ? '#3b82f6' : 'rgba(0,0,0,0.08)'}`,
                    }}>
                    {l}
                  </Box>
                ))}
              </Box>
              {(() => {
                const cd = route.computedRoute?.countryDistances;
                const multiCountry = cd && Object.keys(cd).length > 1;
                return (
                  <Box style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, opacity: multiCountry ? 0.5 : 1 }}>
                    <Typography style={{ fontSize: '0.68rem', color: '#64748b', flex: 1 }}>
                      {multiCountry ? t(lang, 'pricePerCountry') : t(lang, 'pricePerLitre')}
                    </Typography>
                    <Box style={{ display: 'flex', alignItems: 'center', border: '1px solid #e2e8f0', borderRadius: 6, overflow: 'hidden', background: multiCountry ? '#f1f5f9' : '#fff' }}>
                      <Typography style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', padding: '4px 7px', background: '#f8fafc', borderRight: '1px solid #e2e8f0' }}>
                        {getCurrencySymbol(currency)}
                      </Typography>
                      <input type="number" step="0.01" min="0" value={multiCountry ? '' : priceInput} disabled={multiCountry}
                        onChange={e => { setPriceInput(e.target.value); const val = parseFloat(e.target.value); if (!isNaN(val) && val > 0) dispatch(setFuelPrice(val)); }}
                        onBlur={() => { const val = parseFloat(priceInput); const safe = isNaN(val) || val <= 0 ? defaultPrice : val; setPriceInput(String(safe)); dispatch(setFuelPrice(safe)); saveFuelPrice(currency, fuelType, safe); }}
                        style={{ border: 'none', padding: '4px 8px', fontSize: '0.78rem', color: multiCountry ? '#94a3b8' : '#0f172a', outline: 'none', width: 68, cursor: multiCountry ? 'not-allowed' : 'text' }}
                      />
                    </Box>
                  </Box>
                );
              })()}

              {opts.vehicleType !== 'car' && (<>
                <Typography style={{ fontSize: '0.6rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>{t(lang, 'truckDimensions')}</Typography>
                <Box style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 8 }}>
                  {[
                    { key: 'truckWeight', label: 'Peso (t)', placeholder: 'ex: 40' },
                    { key: 'truckAxleLoad', label: 'Eixo (t)', placeholder: 'ex: 11.5' },
                    { key: 'truckHeight', label: 'Altura (m)', placeholder: 'ex: 4.0' },
                    { key: 'truckWidth', label: 'Largura (m)', placeholder: 'ex: 2.55' },
                    { key: 'truckLength', label: 'Comprim. (m)', placeholder: 'ex: 18.75' },
                  ].map(({ key, label, placeholder }) => (
                    <Box key={key} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Typography style={{ fontSize: '0.57rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</Typography>
                      <input type="number" placeholder={placeholder} value={opts[key] || ''}
                        onChange={e => dispatch(setRouteOptions({ [key]: e.target.value }))}
                        style={{ border: '1px solid #e2e8f0', borderRadius: 6, padding: '4px 8px', fontSize: '0.75rem', color: '#0f172a', outline: 'none', width: '100%', boxSizing: 'border-box' }}
                      />
                    </Box>
                  ))}
                </Box>
              </>)}

              <Typography style={{ fontSize: '0.6rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2, marginTop: 2 }}>{t(lang, 'avoidSection')}</Typography>
              {[
                { key: 'avoidTolls',     label: t(lang, 'avoidTolls'),    tip: t(lang, 'avoidTollsTip') },
                { key: 'avoidMotorways', label: t(lang, 'avoidMotorways'), tip: t(lang, 'avoidMotorwaysTip') },
                { key: 'avoidTunnels',   label: t(lang, 'avoidTunnels'),  tip: t(lang, 'avoidTunnelsTip') },
              ].map(({ key, label, tip }) => (
                <Tooltip key={key} title={tip} arrow placement="right">
                  <FormControlLabel style={{ margin: '0 0 0 -6px', display: 'flex' }}
                    control={<Switch size="small" checked={!!opts[key]} onChange={() => toggleOpt(key)} color="primary" />}
                    label={<Typography style={{ fontSize: '0.74rem', color: '#475569' }}>{label}</Typography>}
                  />
                </Tooltip>
              ))}

              {/* Avoid Low Emission Zones (LEZ) */}
              <FormControlLabel
                control={<Checkbox size="small" checked={!!opts.avoidLEZ} onChange={e => dispatch(setRouteOptions({ avoidLEZ: e.target.checked }))} style={{ padding: 4, color: '#22c55e' }} />}
                label={<Typography style={{ fontSize: '0.74rem', color: '#475569' }}>
                  {lang === 'pt' ? 'Evitar Zonas Baixas Emissões (LEZ)' : 'Avoid Low Emission Zones (LEZ)'}
                  <span style={{ fontSize: '0.58rem', color: '#94a3b8', marginLeft: 4 }}>
                    {lang === 'pt' ? '— zonas regulamentadas na Europa' : '— regulated zones in Europe'}
                  </span>
                </Typography>}
              />

              {/* Evitar zonas urbanas é uma OPÇÃO de rota, não uma
                  capacidade vendável — módulo removido do catálogo. */}
              <>
                <Box style={{ marginTop: 8, background: '#fafbfc', borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                  <Box
                    onClick={() => setOpenCountries(o => ({ ...o, __section: !o.__section }))}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
                      cursor: 'pointer', userSelect: 'none',
                    }}>
                    <Box style={{ width: 24, height: 24, borderRadius: 6, background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <WarningIcon style={{ fontSize: 13, color: '#ef4444' }} />
                    </Box>
                    <Typography style={{ fontSize: '0.68rem', fontWeight: 700, color: '#1e293b', flex: 1 }}>
                      {t(lang, 'avoidUrbanAreas')}
                    </Typography>
                    {(opts.avoidUrbanAreas || []).length > 0 && (
                      <Chip size="small" label={(opts.avoidUrbanAreas || []).length} style={{ height: 18, fontSize: '0.52rem', fontWeight: 700, background: '#ef4444', color: '#fff' }} />
                    )}
                    {openCountries.__section ? <ExpandLessIcon style={{ fontSize: 16, color: '#94a3b8' }} /> : <ExpandMoreIcon style={{ fontSize: 16, color: '#94a3b8' }} />}
                  </Box>
                  <Collapse in={!!openCountries.__section}>
                    <Box style={{ padding: '0 12px 10px', borderTop: '1px solid #f1f5f9' }}>
                {Object.entries(URBAN_AREAS_BY_COUNTRY).map(([country, cities]) => {
                  const cityNames = Object.keys(cities);
                  const selected = opts.avoidUrbanAreas || [];
                  const selCount = cityNames.filter(c => selected.includes(c)).length;
                  const allSel = selCount === cityNames.length;
                  const someSel = selCount > 0 && !allSel;
                  const isOpen = !!openCountries[country];
                  function toggleCountryAll(e) { e.stopPropagation(); const rest = selected.filter(c => !cityNames.includes(c)); dispatch(setRouteOptions({ avoidUrbanAreas: allSel ? rest : [...rest, ...cityNames] })); }
                  function toggleCity(city) { const isSel = selected.includes(city); dispatch(setRouteOptions({ avoidUrbanAreas: isSel ? selected.filter(c => c !== city) : [...selected, city] })); }
                  return (
                    <Box key={country} style={{ marginBottom: 2 }}>
                      <Box onClick={() => setOpenCountries(o => ({ ...o, [country]: !o[country] }))}
                        style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', borderRadius: 6, padding: '1px 2px', userSelect: 'none' }}>
                        <Checkbox size="small" checked={allSel} indeterminate={someSel} onClick={toggleCountryAll} color="primary" style={{ padding: 2 }} />
                        <Typography style={{ flex: 1, fontSize: '0.72rem', fontWeight: 600, color: '#334155' }}>{country}</Typography>
                        {selCount > 0 && <Typography style={{ fontSize: '0.6rem', color: '#ef4444', fontWeight: 700, marginRight: 4 }}>{selCount}</Typography>}
                        {isOpen ? <ExpandLessIcon style={{ fontSize: 14, color: '#94a3b8' }} /> : <ExpandMoreIcon style={{ fontSize: 14, color: '#94a3b8' }} />}
                      </Box>
                      <Collapse in={isOpen}>
                        <Box style={{ display: 'flex', flexWrap: 'wrap', gap: 3, paddingLeft: 28, paddingBottom: 4 }}>
                          {cityNames.map(city => { const isSel = selected.includes(city); return (
                            <Box key={city} onClick={() => toggleCity(city)} style={{ padding: '2px 7px', borderRadius: 10, cursor: 'pointer', fontSize: '0.63rem', fontWeight: 600, background: isSel ? '#ef4444' : 'rgba(0,0,0,0.04)', color: isSel ? '#fff' : '#64748b', border: `1px solid ${isSel ? '#ef4444' : 'rgba(0,0,0,0.1)'}` }}>{city}</Box>
                          ); })}
                        </Box>
                      </Collapse>
                    </Box>
                  );
                })}
                {opts.avoidUrbanAreas?.length > 0 && <Typography style={{ fontSize: '0.62rem', color: '#f59e0b', marginTop: 4 }}>⚠ {t(lang, 'avoidCitiesWarning')}</Typography>}
                    </Box>
                  </Collapse>
                </Box>
              </>

              {/* ── Currency — moved below avoid section ──
                  Era uma grid horizontal de chips (5 moedas). Com 40+ moedas
                  passou a Select para não rebentar o layout do diálogo. */}
              <Typography style={{ fontSize: '0.6rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4, marginTop: 10 }}>{t(lang, 'currencyCosts')}</Typography>
              {(() => {
                const activeCode = opts.currency || 'EUR';
                const active = CURRENCIES.find(c => c.code === activeCode) || CURRENCIES[0];
                return (
                  <Box
                    onClick={() => {
                      // Abrir o select via click no card inteiro — refª nativa
                      const el = document.getElementById('currency-select-trigger');
                      if (el) el.click();
                    }}
                    style={{
                      marginBottom: 6,
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '8px 12px',
                      background: '#fff',
                      border: '1px solid rgba(0,0,0,0.08)',
                      borderRadius: 10,
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                      position: 'relative',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.background = '#fafbfc'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(0,0,0,0.08)'; e.currentTarget.style.background = '#fff'; }}
                  >
                    <Box style={{
                      width: 30, height: 22, borderRadius: 5,
                      background: '#f1f5f9', border: '1px solid #e2e8f0',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '0.9rem', flexShrink: 0,
                    }}>
                      {active.flag}
                    </Box>
                    <Box style={{ flex: 1, minWidth: 0 }}>
                      <Typography style={{ fontSize: '0.74rem', fontWeight: 700, color: '#0f172a', lineHeight: 1.2 }}>
                        {active.symbol} <span style={{ color: '#64748b', fontWeight: 600 }}>· {active.label}</span>
                      </Typography>
                      <Typography style={{ fontSize: '0.58rem', color: '#94a3b8', fontWeight: 600, marginTop: 1 }}>
                        {active.code}
                      </Typography>
                    </Box>
                    <Select
                      id="currency-select-trigger"
                      value={activeCode}
                      onChange={(e) => dispatch(setRouteOptions({ currency: e.target.value }))}
                      disableUnderline
                      MenuProps={{ PaperProps: { style: { maxHeight: 360 } } }}
                      IconComponent={(props) => (
                        <Box {...props} style={{ ...props.style, right: 8, color: '#94a3b8', pointerEvents: 'none' }}>
                          ▾
                        </Box>
                      )}
                      style={{
                        position: 'absolute', inset: 0, opacity: 0,
                        width: '100%', height: '100%',
                      }}
                    >
                      {CURRENCIES.map(({ code, symbol, label, flag }) => (
                        <MenuItem key={code} value={code} style={{ fontSize: '0.78rem' }}>
                          <span style={{ marginRight: 8, fontSize: '0.95rem' }}>{flag}</span>
                          <span style={{ fontWeight: 700, marginRight: 6, color: '#0f172a', minWidth: 28 }}>{symbol}</span>
                          <span style={{ color: '#475569' }}>{label}</span>
                          <span style={{ color: '#94a3b8', marginLeft: 'auto', fontSize: '0.66rem', fontWeight: 600 }}>{code}</span>
                        </MenuItem>
                      ))}
                    </Select>
                    <Box style={{ color: '#94a3b8', fontSize: '0.7rem', pointerEvents: 'none' }}>▾</Box>
                  </Box>
                );
              })()}

              {/* ── Combustível ─────────────────────────────── */}
              <Box style={{ marginTop: 14 }}>
                <Typography style={{ fontSize: '0.68rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
                  {lang === 'pt' ? 'Combustível' : 'Fuel'}
                </Typography>
                <Box style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
                  {FUEL_TYPES.map(ft => {
                    const active = (route.fuelType || 'diesel') === ft.value;
                    return (
                      <Box key={ft.value} onClick={() => dispatch(setFuelType(ft.value))}
                        style={{
                          flex: 1, textAlign: 'center', padding: '6px 4px', borderRadius: 8, cursor: 'pointer',
                          fontSize: '0.62rem', fontWeight: 600,
                          background: active ? '#dbeafe' : 'rgba(0,0,0,0.03)',
                          color: active ? '#2563eb' : '#64748b',
                          border: `1px solid ${active ? '#3b82f6' : 'rgba(0,0,0,0.06)'}`,
                        }}>
                        {ft.value === 'diesel' ? 'Gasóleo' : ft.value === 'gasoline' ? 'Gasolina' : ft.value === 'gnl' ? 'GNL' : 'Eléctrico'}
                      </Box>
                    );
                  })}
                </Box>
                {/* Auto prices checkbox */}
                <Box
                  onClick={() => dispatch(setRouteOptions({ ...opts, autoFuelPrices: opts.autoFuelPrices === false ? true : false }))}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', marginBottom: 8, padding: '4px 0' }}
                >
                  <Box style={{
                    width: 16, height: 16, borderRadius: 4,
                    border: `1.5px solid ${opts.autoFuelPrices !== false ? '#3b82f6' : '#cbd5e1'}`,
                    background: opts.autoFuelPrices !== false ? '#3b82f6' : '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {opts.autoFuelPrices !== false && <span style={{ color: '#fff', fontSize: 10, fontWeight: 700 }}>✓</span>}
                  </Box>
                  <Typography style={{ fontSize: '0.65rem', fontWeight: 600, color: '#1e293b' }}>
                    {lang === 'pt' ? 'Preços automáticos por país (EU Oil Bulletin)' : 'Auto prices by country (EU Oil Bulletin)'}
                  </Typography>
                </Box>

                {opts.autoFuelPrices === false ? (() => {
                  // Em rotas que atravessam mais do que 1 país, o preço único da
                  // Sidebar não é aplicado — cada país usa o seu (Preferências
                  // ou EU Oil Bulletin). Aqui sinalizamos isso visualmente.
                  const cdSide = route.computedRoute?.countryDistances;
                  const isMultiCountrySide = cdSide && Object.keys(cdSide).length > 1;
                  return (
                    <>
                      <Box style={{ display: 'flex', gap: 6 }}>
                        <TextField size="small" variant="outlined"
                          label={lang === 'pt' ? `Consumo (${(route.fuelType || 'diesel') === 'electric' ? 'kWh' : 'L'}/100km)` : `Consumption`}
                          type="number"
                          value={route.fuelConsumption || ''}
                          onChange={e => dispatch(setFuelConsumption(e.target.value ? Number(e.target.value) : null))}
                          style={{ flex: 1 }}
                          inputProps={{ style: { fontSize: '0.72rem', padding: '7px 10px' }, step: '0.1' }}
                          InputLabelProps={{ style: { fontSize: '0.65rem' } }}
                        />
                        <Tooltip
                          title={isMultiCountrySide
                            ? (lang === 'pt'
                              ? 'Rota multi-país — cada país usa o seu próprio preço. Configura em Preferências → Combustível.'
                              : 'Multi-country route — each country uses its own price. Configure in Preferences → Fuel.')
                            : ''}
                          arrow placement="top"
                        >
                          <span style={{ flex: 1 }}>
                            <TextField size="small" variant="outlined"
                              label={lang === 'pt'
                                ? (isMultiCountrySide ? 'Preço — por país' : 'Preço (€/L)')
                                : (isMultiCountrySide ? 'Price — per country' : 'Price (€/L)')}
                              type="number"
                              value={isMultiCountrySide ? '' : (route.fuelPrice || '')}
                              onChange={e => dispatch(setFuelPrice(e.target.value ? Number(e.target.value) : null))}
                              disabled={isMultiCountrySide}
                              fullWidth
                              inputProps={{ style: { fontSize: '0.72rem', padding: '7px 10px' }, step: '0.01' }}
                              InputLabelProps={{ style: { fontSize: '0.65rem' } }}
                            />
                          </span>
                        </Tooltip>
                      </Box>
                      {isMultiCountrySide && (
                        <Box style={{
                          marginTop: 6, padding: '6px 8px',
                          background: '#f0f9ff', border: '1px solid #e0f2fe',
                          borderRadius: 6, display: 'flex', gap: 6, alignItems: 'flex-start',
                        }}>
                          <span style={{ fontSize: '0.78rem', lineHeight: 1 }}>ℹ️</span>
                          <Typography style={{ fontSize: '0.62rem', color: '#0c4a6e', lineHeight: 1.45 }}>
                            {lang === 'pt'
                              ? <>Rota com <b>{Object.keys(cdSide).length} países</b> — cada um usa o seu preço (Preferências → Combustível ou EU Oil Bulletin).</>
                              : <>Route with <b>{Object.keys(cdSide).length} countries</b> — each uses its own price (Preferences → Fuel or EU Oil Bulletin).</>}
                          </Typography>
                        </Box>
                      )}
                    </>
                  );
                })() : (
                  <>
                    {/* Average consumption is vehicle-dependent — always visible even when
                        fuel prices come from the EU bulletin. */}
                    <Box style={{ marginBottom: 8 }}>
                      <TextField size="small" variant="outlined" fullWidth
                        label={lang === 'pt' ? `Consumo médio (${(route.fuelType || 'diesel') === 'electric' ? 'kWh' : 'L'}/100km)` : `Average consumption`}
                        type="number"
                        value={route.fuelConsumption || ''}
                        onChange={e => dispatch(setFuelConsumption(e.target.value ? Number(e.target.value) : null))}
                        inputProps={{ style: { fontSize: '0.72rem', padding: '7px 10px' }, step: '0.1' }}
                        InputLabelProps={{ style: { fontSize: '0.65rem' } }}
                      />
                    </Box>
                    <Box style={{ padding: '8px 10px', borderRadius: 8, background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                      <Typography style={{ fontSize: '0.62rem', fontWeight: 600, color: '#16a34a' }}>
                        {lang === 'pt' ? '✓ Preços reais por país serão usados automaticamente' : '✓ Real prices per country will be used automatically'}
                      </Typography>
                      <Typography style={{ fontSize: '0.55rem', color: '#64748b', marginTop: 2 }}>
                        {lang === 'pt' ? 'Fonte: European Commission Weekly Oil Bulletin' : 'Source: European Commission Weekly Oil Bulletin'}
                      </Typography>
                    </Box>
                  </>
                )}
              </Box>
            </>)}


          </Box>
        </Paper>,
        document.body
      )}

      {route.status === 'error' && (
        <Typography style={{ fontSize: '0.72rem', color: '#ef4444', textAlign: 'center' }}>
          {route.errorMessage || t(lang, 'error')}
        </Typography>
      )}

      {/* O aviso da rota (ex: provider escolhido pelo modo híbrido,
          fallback de provider, áreas urbanas que não foram evitadas)
          ficou movido para um ícone discreto no header da secção Rota,
          em [route header — RotaInfoBadge]. Mantém a info acessível
          mas não ocupa espaço vertical. */}

      {!process.env.REACT_APP_MAPBOX_TOKEN?.startsWith('pk.') && (
        <Box style={{
          padding: '8px 10px',
          background: 'rgba(245,158,11,0.08)',
          border: '1px solid rgba(245,158,11,0.25)',
          borderRadius: 6,
        }}>
          <Typography style={{ fontSize: '0.65rem', color: '#d97706' }}>
            ⚠ {t(lang, 'noTokenWarning')}
          </Typography>
        </Box>
      )}

      {/* Route Compare Dialog — rendered via portal to avoid sidebar transform stacking */}
      {ReactDOM.createPortal(
        <RouteCompareDialog
          open={compareOpen}
          onClose={() => {
            // Fecha mantendo a rota atualmente aplicada; limpa as alternativas
            // (o mapa fica só com a rota escolhida) e o preview.
            const allRoutes = compareAllRoutesRef.current;
            const idx = selectedCompareIdx != null ? selectedCompareIdx : bestRouteIdx;
            if (allRoutes && allRoutes[idx]) {
              const routeData = allRoutes[idx];
              const useTacho = route.routeOptions?.useTachograph === true;
              const stops = useTacho ? calculateStops(routeData.duration, routeData.distance, route.departureTime) : [];
              dispatch(setRouteResult({ computedRoute: routeData, stops, alternatives: [] }));
            }
            setCompareOpen(false);
            setSelectedCompareIdx(null);
            dispatch(setHoveredCompareRouteIdx(null));
            dispatch(setPreviewRoute(null));
            dispatch(setDetailsPanelOpen(false));
          }}
          routes={compareRoutes}
          bestRouteIdx={bestRouteIdx}
          selectedIdx={selectedCompareIdx}
          lang={lang}
          onConfirm={() => {
            // Confirma a rota escolhida como definitiva: fica só ela no mapa
            // (sem alternativas) e o diálogo fecha.
            const allRoutes = compareAllRoutesRef.current;
            const idx = selectedCompareIdx != null ? selectedCompareIdx : bestRouteIdx;
            if (allRoutes && allRoutes[idx]) {
              const routeData = allRoutes[idx];
              const useTacho = route.routeOptions?.useTachograph === true;
              const stops = useTacho ? calculateStops(routeData.duration, routeData.distance, route.departureTime) : [];
              dispatch(setRouteResult({ computedRoute: routeData, stops, alternatives: [] }));
            }
            setCompareOpen(false);
            setSelectedCompareIdx(null);
            dispatch(setHoveredCompareRouteIdx(null));
            dispatch(setPreviewRoute(null));
            dispatch(setDetailsPanelOpen(false));
          }}
          onSelectRoute={(idx) => {
            const allRoutes = compareAllRoutesRef.current;
            if (!allRoutes || !allRoutes[idx]) return;
            const routeData = allRoutes[idx];
            // Alinhado com o gate principal — opt-in estrito por route option.
            const useTacho = route.routeOptions?.useTachograph === true;
            const stops = useTacho ? calculateStops(routeData.duration, routeData.distance, route.departureTime) : [];
            // Aplica a rota escolhida como PRINCIPAL e mantém as outras como
            // alternativas (o mapa continua a mostrá-las a tracejado). NÃO fecha
            // o diálogo — o operador pode continuar a experimentar até Confirmar.
            const others = allRoutes.filter((_, i) => i !== idx);
            dispatch(setRouteResult({ computedRoute: routeData, stops, alternatives: others }));
            setSelectedCompareIdx(idx);
            dispatch(setHoveredCompareRouteIdx(null));
            dispatch(setPreviewRoute(null));
          }}
          onHoverRoute={(idx) => {
            dispatch(setHoveredCompareRouteIdx(idx));
            if (idx != null) {
              const allRoutes = compareAllRoutesRef.current;
              if (allRoutes && allRoutes[idx]) {
                const r = allRoutes[idx];
                const cd = compareRoutes[idx];
                dispatch(setPreviewRoute({
                  distance: r.distance,
                  duration: r.duration,
                  geometry: r.geometry,
                  roadBreakdown: r.roadBreakdown,
                  countryDistances: r.countryDistances,
                  fuelCost: cd?.fuelCost,
                  tollCost: cd?.tollCost,
                  totalCost: cd?.totalCost,
                  label: cd?.label,
                  color: cd?.color,
                }));
              }
            } else {
              dispatch(setPreviewRoute(null));
            }
          }}
          loading={compareLoading}
          currencySymbol={getCurrencySymbol(clientSettings?.defaultCurrency || 'EUR')}
        />,
        document.body
      )}

      {/* Add-OT-to-route dialog — disponível quando a rota é draft. */}
      <AddOTToRouteDialog
        open={addOTDialogOpen}
        routeId={route.dbId}
        routeName={route.name || route.primaryRef || `Rota #${route.dbId}`}
        routeStatus={route.dbStatus}
        onClose={() => setAddOTDialogOpen(false)}
        onAdded={() => setAddOTDialogOpen(false)}
      />
    </Box>
  );
}

export default RouteForm;
